import { useState, useEffect, useRef } from "react";
import { useTheme } from "../components/ThemeProvider";
import axios from "axios";
import { dialogs } from "../context/DialogContext";
import usePermiso from "../hooks/usePermiso";

const API = import.meta.env.VITE_API_URL || "http://localhost:4000/api";

const PRIVILEGIOS_VACIOS = {
  global: { id: 1, ana_activa_global: true, motivo: "" },
  procesos: [],
  agentes_bloqueados: [],
  contactos_bloqueados: [],
  agentes_disponibles: [],
  catalogo_procesos: [],
};

const WORK_RULES_DEFAULT = {
  grupos: [],
  procesos: [],
  eventos: [],
  campos_condicion: [],
  operadores: [],
  tipos_accion: [],
  tipos_delay: [],
};

const WEEKDAY_OPTIONS = [
  { key: "lunes", label: "L" },
  { key: "martes", label: "M" },
  { key: "miercoles", label: "X" },
  { key: "jueves", label: "J" },
  { key: "viernes", label: "V" },
  { key: "sabado", label: "S" },
  { key: "domingo", label: "D" },
];

const CHANNEL_OPTIONS = [
  { key: "telefono", label: "Teléfono" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "email", label: "Email" },
];

const FALLBACK_CHANNEL_OPTIONS = [
  { key: "", label: "Sin medio alternativo" },
  ...CHANNEL_OPTIONS,
];

const RESPONSE_TIMING_OPTIONS = [
  { key: "immediate", label: "Enseguida" },
  { key: "after_minutes", label: "Tras X minutos" },
  { key: "at_time", label: "A una hora concreta" },
  { key: "next_business_day_at", label: "Siguiente laborable a las" },
];

const AGENDA_SLOT_BASIS_OPTIONS = [
  { key: "same_day_if_possible", label: "Mismo día si se puede" },
  { key: "next_day", label: "Día siguiente" },
  { key: "next_business_day", label: "Siguiente laborable" },
  { key: "second_business_day", label: "Segundo día laborable" },
];

function buildLeadTimeRange(from = "09:30", to = "20:00") {
  return {
    id: `range_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    from,
    to,
  };
}

function buildLeadContactStep(order, channel = "telefono", timingType = "immediate", timingValue = "", fallbackChannel = "whatsapp") {
  return {
    id: `step_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    order,
    channel,
    timingType,
    timingValue,
    fallbackChannel,
  };
}

function normalizeFallbackChannel(value, defaultValue = "whatsapp") {
  if (value === "") return "";
  if (value == null) return defaultValue;
  const normalized = String(value).trim();
  return normalized === "" ? "" : normalized;
}

function splitTextareaLines(value) {
  return String(value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function buildLeadResponseWindow(from = "09:30", to = "20:00", contactSteps = null) {
  return {
    id: `window_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    from,
    to,
    contactSteps: contactSteps || [
      buildLeadContactStep(1, "telefono", "immediate", "", "whatsapp"),
    ],
  };
}

const LEAD_PROCESS_DEFAULT = {
  macroProceso: "atencion_leads",
  nombre: "Atención de leads",
  descripcion: "Proceso único multicanal de atención, visita, agenda y continuidad.",
  commercialPolicy: {
    maxInformativeAnswersBeforeVisit: 2,
    askForMoreQuestionsOnlyOnce: true,
    pushVisitWhenClientTriesToClose: true,
    forbidPassiveCloseBeforeVisit: true,
    openingStyle: "specific_property_first",
    firstFollowUpInstruction: 'Tras la primera duda respondida, puedes cerrar con "¿Alguna otra pregunta?" o "¿Qué más te gustaría saber?" una sola vez.',
    followUpAfterFirstAnswerInstruction: "Ya has resuelto al menos una duda: avanza hacia la visita con naturalidad.",
    visitPivotInstruction: "Tras la duda informativa número {{maxAnswers}}, NO sigas en modo informativo: propone directamente la visita con una frase breve y natural.",
    passiveCloseSignals: ["gracias", "solo eso", "ya lo pienso", "te digo algo"],
    passiveCloseInstruction: 'Si el cliente intenta cerrar con "{{signals}}" o similar antes de que hayas intentado llevarlo a visita, enlaza con una propuesta breve de visita.',
    passiveCloseProhibition: "PROHIBIDO cerrar en modo pasivo si aún no has intentado mover la conversación al siguiente paso comercial.",
    discardToTelemarketingInstruction: "Si el cliente descarta la propiedad, activa el modo T2 para encontrar otra opción sin despedirte.",
  },
  channels: {
    telefono: { enabled: true, firstContact: true, tone: "natural_corto", styleNotes: "Frases muy cortas, humanas y directas." },
    whatsapp: { enabled: true, firstContact: true, tone: "natural_texto", styleNotes: "Mensajes cortos, claros y comerciales." },
    email: { enabled: true, firstContact: false, tone: "profesional_breve", styleNotes: "Correo breve orientado a mover al siguiente paso." },
  },
  agendaPolicy: {
    useConcreteSlots: true,
    proposeAvailabilityWindow: false,
    firstProposalStrategy: "next_slot",
    fallbackProposalStrategy: "second_slot",
    offerConcreteSlotsInstruction: "Propón una hora concreta directamente sin preguntar qué día le viene bien.",
    secondOptionInstruction: "Propón primero la Opción 1. Si la rechaza, propón la Opción 2.",
    rejectBothInstruction: "Si rechaza las dos, pide al cliente que proponga él.",
    exactDayInstruction: 'IMPORTANTE: Di el día exacto tal como aparece aquí, no uses "mañana" salvo que la etiqueta lo diga explícitamente.',
  },
  languagePolicy: {
    defaultLanguage: "español",
    useLeadLanguageWhenAvailable: true,
    mirrorClientLanguage: true,
    fallbackToDefaultWhenUnknown: true,
    sameLanguageInstruction: "Si el lead ya tiene un idioma informado, empieza en ese idioma. Si el cliente escribe o habla en otro idioma de forma clara, cambia a ese idioma y mantén toda la conversación ahí.",
    defaultLanguageInstruction: "Si no hay ninguna señal de idioma, responde por defecto en español.",
    dontMixLanguagesInstruction: "Nunca mezcles idiomas en la misma respuesta.",
    unsupportedLanguageInstruction: "Si el idioma detectado no está entre los idiomas soportados, usa inglés como idioma puente.",
  },
  assignmentPolicy: {
    enabled: true,
    prioritizeLeadLanguage: true,
    bridgeLanguage: "en",
    keepExistingClientWithSameAgent: true,
    existingClientMatchBy: ["email", "telefono"],
    prioritizeExclusivePropertyCaptor: true,
    excludeAgentsFromGeneralPool: ["Ángel"],
    directCaptorAgents: ["Ángel"],
    respectAgentMaxLeads: true,
    captorPriorityMaxLeadGap: 10,
    prioritizeCaptorWhenAvailable: true,
    localProvinceIds: [3, 46],
    metroProvinceIds: [28, 8],
    metroValenciaProvinceId: 46,
    metroValenciaLocalities: ["valencia", "valència"],
    metroAgents: ["Rita", "Sabrina"],
    remoteProvinceAgents: ["Antonia", "Josefa"],
    fallbackStrategy: "least_loaded",
  },
  voicePolicy: {
    useStateMachineForOutboundCalls: true,
    turnSilenceMs: 750,
    userSilenceTimeoutSeconds: 4,
    askAnotherQuestionPrompt: "¿Alguna otra pregunta sobre la vivienda?",
    visitOfferPrompt: "Perfecto. ¿Te gustaría concertar una visita para verla en persona?",
    concreteSlotOfferPrompt: "Perfecto. Te propongo {{slot1}}{{slot2Clause}}",
    concreteSlotSecondOptionClause: " Si no, también podría ser {{slot2}}.",
    concreteSlotFallbackPrompt: "Si esas opciones no te encajan, dime qué momento te viene mejor y lo buscamos.",
    silenceRecoveryPrompt: "Sigo aquí.",
    silenceRecoveryQuestionPrompt: "Si quieres, dime.",
    silenceRecoveryVisitPrompt: "Si te parece, te propongo una visita.",
    silenceRecoverySlotPrompt: "Si quieres, te repito la opción.",
    postVisitT2IntroPrompt: "Perfecto. Y antes de que nos veamos, {{firstQuestion}}",
    realtimeRules: [
      "Si el texto llega mal transcrito, interprétalo en contexto inmobiliario.",
      "Máximo 2 frases cortas. En teléfono las respuestas largas aburren.",
      "Habla de forma natural y cercana, como una persona real.",
      "Solo texto plano, sin listas ni formato. Listo para ser leído en voz alta.",
      "Si el cliente quiere info de una propiedad, dile que le mandas los detalles por WhatsApp.",
      "Si quiere visitar, ofrécele una cita concreta.",
      "Objetivo: conseguir que el lead acepte una visita o deje sus datos.",
    ],
    outboundChannelInstruction: "En cuanto el cliente conteste, habla tú primero: no esperes en silencio.",
    outboundStyleInstruction: "Mismo criterio que en WhatsApp (misma plantilla/tarea) pero oral: frases muy cortas, una pregunta clara.",
    avoidRepeatLeadDataInstruction: "No pidas al cliente que repita lo que ya figura en los datos del lead.",
    avoidPassiveCloseInstruction: "En llamada sé resolutiva: no cierres de forma amable-pasiva si todavía no has intentado llevar la conversación a visita.",
    avoidGenericOpeningInstruction: 'PROHIBIDO abrir con fórmulas genéricas como "¿Qué te gustaría saber?" o "dime qué quieres saber".',
    knownLeadOpeningInstruction: "Ya conoces a este cliente o existe historial en el CRM. Habla con cercanía y naturalidad. NO te presentes como si fuera la primera vez. Si en las notas del lead hay una pregunta concreta, empieza por ahí.",
    newLeadOpeningInstruction: "Es un lead nuevo. Preséntate de forma breve y humana como Ana, asesora virtual de VitalCasa. Si conoces el nombre del cliente, úsalo. Si hay una referencia o propiedad concreta, menciónala en la apertura. Si en las notas del lead hay una pregunta concreta, empieza por esa pregunta o enlázala en la primera intervención.",
    callObjectiveInstructions: [
      "Si el lead contiene una pregunta concreta sobre la vivienda, tu primera respuesta debe atender a esa duda.",
      "Si no hay una pregunta concreta y sí hay propiedad o referencia, abre con naturalidad diciendo que preguntas por esa vivienda y pregunta qué necesita saber.",
      "Si no hay referencia ni pregunta concreta, abre de forma general y averigua qué necesita.",
      "Objetivo comercial de la llamada: resolver dudas, proponer tú misma una cita concreta y dejar la visita encaminada dentro de la propia llamada.",
      "Si el cliente solo quiere un dato rápido, dáselo sin rodeos; no vuelvas a abrir otra fase larga de preguntas.",
      "Después de resolver dos dudas, gira a visita con naturalidad y sin despedirte.",
      "Usa exactamente el mismo criterio comercial y de preguntas por orden que el prompt central de atención de leads.",
    ],
    openingGenerationInstruction: "La apertura debe sonar breve, humana y comercial, no como un operador de atención al cliente.",
  },
  telemarketingPolicy: {
    modeLabel: "MODO T2 — CONOCER AL CLIENTE",
    activationInstruction: "Se activa (1) cuando el cliente descarta la propiedad, o (2) después de confirmar una visita.",
    discardHookTemplate: "[Frase corta que reconoce el descarte]. Tengo bastante movimiento en [POBLACIÓN del lead] con [TIPO del lead], a ver si damos con algo que te encaje mejor. [Primera pregunta T2 según el tipo de inmueble del lead]",
    discardExamples: [
      'Lead de casa en Ondara -> "Sin problema. Tengo bastante movimiento en Ondara con casas, a ver si damos con algo que te encaje mejor. ¿Qué presupuesto manejas?"',
      'Lead de parcela en Pedreguer -> "Entendido. Tengo bastante movimiento en Pedreguer con parcelas, a ver si damos con algo que te encaje mejor. ¿Buscas parcela urbana o rústica?"',
      'Lead de apartamento en Dénia -> "Sin problema. Tengo bastante movimiento en Dénia con apartamentos, a ver si damos con algo que te encaje mejor. ¿Cuántos dormitorios necesitas?"',
    ],
    commonRules: "NO preguntes si quiere seguir. NO te despidas. Haz solo esa pregunta y espera respuesta. Si el cliente responde, continúa con T2 de forma natural siguiendo la guía. No preguntes zona si ya sabes el municipio. No preguntes operación. Consulta en tiempo real cuántas propiedades encajan. Para cuando tengas 10 o menos, deja de preguntar y preséntelas. Si llegas a 0, sugiere relajar algún filtro.",
    afterVisitConfirmationInstruction: 'INSTRUCCIÓN CRÍTICA — PRIORIDAD MÁXIMA — ANULA CUALQUIER REGLA DE DESPEDIDA: La visita está registrada para el {{fechaHumana}} a las {{horaHumana}}. PROHIBIDO despedirse. PROHIBIDO cerrar la conversación. OBLIGATORIO continuar hablando. Después de confirmar la cita, di EXACTAMENTE algo como "Perfecto, y antes de que nos veamos, ¿me permites hacerte un par de preguntas rápidas?" y entra en modo T2 — CONOCER AL CLIENTE. Recoge sus criterios de búsqueda siguiendo la guía T2 (el orden depende del tipo de propiedad). Una pregunta por mensaje, de forma natural. No preguntes zona si ya sabes el municipio. No preguntes operación. Consulta en tiempo real cuántas propiedades encajan. Para cuando tengas 10 o menos resultados, deja de preguntar y preséntelas. Si llegas a 0, sugiere relajar algún filtro. NO derives al agente. NO te despidas. {{missingDataInstruction}}',
    confirmedVisitModeInstruction: "CITA YA CONFIRMADA para {{fechaHumana}}. Estás en modo TELEMARKETING. Sigue con las preguntas de forma natural. Cuando el cliente no tenga más preguntas, despídete con calidez y dile que le esperas en la visita.",
    confirmedVisitReminder: "RECORDATORIO FINAL: La cita ya está confirmada. Tu tarea ahora es SOLO recoger criterios de búsqueda (modo T2). NO te despidas. NO finalices la conversación. Sigue preguntando hasta completar el perfil del cliente.",
    resultsState: {
      zeroWithFilters: "Con los criterios actuales hay 0 propiedades. Antes de seguir preguntando, dile al cliente que con esos criterios no tienes nada y sugiere relajar algún filtro (zona, precio, extras). Verifica que haya resultados antes de proponer alternativas.",
      tooManyResults: "Hay {{totalResultados}} propiedades con esos criterios; todavía son demasiadas. Sigue recogiendo criterios con naturalidad hasta llegar a 10 o menos.",
      readyToShow: "Hay {{totalResultados}} propiedades que encajan. PARA de preguntar criterios ahora mismo y empieza a presentarlas una a una.",
    },
    guides: {
      unknown: "Aún no sabes qué tipo de propiedad busca.\nPASO 1 — Pregunta el tipo: piso/apartamento, chalet/casa, local comercial, oficina, nave industrial, o terreno/solar.\nUna sola pregunta, espera respuesta.",
      piso: "Si aún no tienes el dato, pregunta en este orden ESTRICTO, uno por mensaje:\n1. Población (si no la sabes)\n2. Nº de dormitorios\n3. Presupuesto máximo\n4. Piscina comunitaria (sí/no)\n5. Ascensor (sí/no)\n6. Garaje (sí/no)\n7. Terraza o balcón (sí/no)\n8. Amueblado (sí/no)\nPuedes combinar dos preguntas en una sola si queda natural. Para cuando tengas 10 o menos resultados.",
      chalet: "Si aún no tienes el dato, pregunta en este orden ESTRICTO, uno por mensaje:\n1. Zona o urbanización (si no la sabes; no preguntes municipio si ya lo tienes)\n2. Presupuesto máximo\n3. Piscina privada (sí/no)\n4. Superficie mínima de parcela\n5. Nº de dormitorios\n6. Amueblado (sí/no)\nPuedes combinar dos preguntas en una sola si queda natural. Para cuando tengas 10 o menos resultados.",
      local: "Si aún no tienes el dato, pregunta en este orden ESTRICTO, uno por mensaje:\n1. Zona o municipio\n2. Presupuesto máximo\n3. Superficie (m²)\n4. Estado del local (reformado, a reformar, nuevo...)\nPara cuando tengas 10 o menos resultados.",
      oficina: "Si aún no tienes el dato, pregunta en este orden ESTRICTO, uno por mensaje:\n1. Zona o municipio\n2. Presupuesto máximo\n3. Superficie (m²)\n4. Aparcamiento (sí/no)\nPara cuando tengas 10 o menos resultados.",
      nave: "Si aún no tienes el dato, pregunta en este orden ESTRICTO, uno por mensaje:\n1. Superficie mínima (m²)\n2. Presupuesto máximo\n3. Altura libre mínima\n4. Muelles de carga (sí/no)\n5. Oficina incorporada (sí/no)\nPara cuando tengas 10 o menos resultados.",
      terreno: "Si aún no tienes el dato, pregunta en este orden ESTRICTO, uno por mensaje:\n1. Calificación: urbano o rústico\n2. Presupuesto máximo\n3. Zona o municipio\n4. Superficie mínima (m²)\n5. Edificabilidad (si necesita dato concreto)\nPara cuando tengas 10 o menos resultados.",
      generic: "Pregunta en este orden (uno por mensaje): población -> presupuesto -> dormitorios -> extras. Para cuando tengas 10 o menos resultados.",
    },
    propertyPresentationRules: [
      "Si hay resultados, preséntalas una a una de forma atractiva y natural.",
      'UNA sola propiedad por turno; pon su referencia en "ref" del JSON, nunca en el texto visible.',
      "OBLIGATORIO: cada mensaje tuyo debe terminar con una frase de cierre. Sin excepciones.",
    ],
  },
  responseRules: [
    {
      id: "laborable_inmediato_telefono",
      name: "Laborable en horario",
      days: ["lunes", "martes", "miercoles", "jueves", "viernes"],
      windows: [
        buildLeadResponseWindow("09:30", "20:00", [
          buildLeadContactStep(1, "telefono", "immediate", "", "whatsapp"),
        ]),
        buildLeadResponseWindow("20:01", "09:29", [
          buildLeadContactStep(1, "whatsapp", "next_business_day_at", "10:00", "telefono"),
        ]),
      ],
      notes: "Si el lead entra en horario laboral, intentar primer contacto por llamada.",
    },
    {
      id: "fin_semana_siguiente_laborable",
      name: "Fin de semana",
      days: ["sabado", "domingo"],
      windows: [
        buildLeadResponseWindow("00:00", "23:59", [
          buildLeadContactStep(1, "whatsapp", "next_business_day_at", "10:00", "telefono"),
        ]),
      ],
      notes: "Responder el siguiente laborable a las 10:00.",
    },
  ],
  agendaRules: [
    {
      id: "agenda_manana",
      name: "Contacto por la mañana",
      days: ["lunes", "martes", "miercoles", "jueves", "viernes"],
      timeFrom: "09:30",
      timeTo: "13:59",
      slotBasis: "next_business_day",
      firstTime: "10:00",
      secondTime: "17:00",
      allowOpenAvailability: false,
      channel: "telefono",
      notes: "Propón primero el siguiente hueco laborable.",
    },
    {
      id: "agenda_tarde_fin_semana",
      name: "Tarde o fin de semana",
      days: ["lunes", "martes", "miercoles", "jueves", "viernes", "sabado", "domingo"],
      timeFrom: "14:00",
      timeTo: "23:59",
      slotBasis: "second_business_day",
      firstTime: "10:00",
      secondTime: "17:00",
      allowOpenAvailability: false,
      channel: "whatsapp",
      notes: "A partir de las 14:00 lleva la propuesta al segundo día laborable para dar margen de preparación.",
    },
  ],
  subphases: [
    { key: "primer_contacto", label: "Primer contacto", objective: "Atender la primera duda con naturalidad.", allowedChannels: ["telefono", "whatsapp", "email"] },
    { key: "dudas", label: "Resolución de dudas", objective: "Resolver dudas breves sin alargar la conversación.", allowedChannels: ["telefono", "whatsapp", "email"] },
    { key: "propuesta_visita", label: "Propuesta de visita", objective: "Llevar la conversación a visita.", allowedChannels: ["telefono", "whatsapp", "email"] },
    { key: "agenda", label: "Agenda", objective: "Proponer huecos concretos y cerrar cita.", allowedChannels: ["telefono", "whatsapp", "email"] },
    { key: "seguimiento", label: "Seguimiento", objective: "Retomar leads sin respuesta.", allowedChannels: ["telefono", "whatsapp", "email"] },
    { key: "postagenda", label: "Post-agenda", objective: "Preparar la visita y completar contexto.", allowedChannels: ["telefono", "whatsapp", "email"] },
    { key: "propietario", label: "Coordinación propietario", objective: "Coordinar disponibilidad con propietario.", allowedChannels: ["telefono", "whatsapp", "email"] },
    { key: "postventa", label: "Postventa", objective: "Continuidad comercial tras visita/venta.", allowedChannels: ["telefono", "whatsapp", "email"] },
  ],
};

function cloneLeadProcessDefault() {
  return JSON.parse(JSON.stringify(LEAD_PROCESS_DEFAULT));
}

function buildLeadResponseRule() {
  return {
    id: `response_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    name: "",
    days: ["lunes", "martes", "miercoles", "jueves", "viernes"],
    windows: [buildLeadResponseWindow("09:30", "20:00")],
    notes: "",
  };
}

function buildLeadAgendaRule() {
  return {
    id: `agenda_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    name: "",
    days: ["lunes", "martes", "miercoles", "jueves", "viernes"],
    timeFrom: "09:30",
    timeTo: "20:00",
    slotBasis: "next_business_day",
    firstTime: "10:00",
    secondTime: "17:00",
    allowOpenAvailability: false,
    channel: "telefono",
    notes: "",
  };
}

function parseLeadProcessConfig(rawValue) {
  if (!rawValue) return cloneLeadProcessDefault();
  try {
    const parsed = typeof rawValue === "string" ? JSON.parse(rawValue) : rawValue;
    const normalizeResponseRule = (rule, index) => {
      const base = buildLeadResponseRule();
      const baseWindow = base.windows[0];
      const baseStep = baseWindow.contactSteps[0];
      const legacyTimeFrom = rule?.timeFrom || baseWindow.from;
      const legacyTimeTo = rule?.timeTo || baseWindow.to;
      const legacyTimingType = rule?.timingType || baseStep.timingType;
      const legacyTimingValue = rule?.timingValue || baseStep.timingValue;
      const legacyPrimaryChannel = rule?.primaryChannel || baseStep.channel;
      const legacySecondaryChannel = normalizeFallbackChannel(rule?.secondaryChannel, baseStep.fallbackChannel);
      const contactSteps = Array.isArray(rule?.contactSteps) && rule.contactSteps.length > 0
        ? rule.contactSteps
            .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
            .map((step, stepIndex) => ({
              id: step.id || `step_${index}_${stepIndex}_${Math.random().toString(36).slice(2, 7)}`,
              order: step.order || stepIndex + 1,
              channel: step.channel || CHANNEL_OPTIONS[stepIndex]?.key || "telefono",
              timingType: step.timingType || "immediate",
              timingValue: step.timingValue || "",
              fallbackChannel: normalizeFallbackChannel(step.fallbackChannel, "whatsapp"),
            }))
        : [
            buildLeadContactStep(1, legacyPrimaryChannel, legacyTimingType, legacyTimingValue, legacySecondaryChannel),
          ];
      const legacyWindows = Array.isArray(rule?.windows) && rule.windows.length > 0
        ? rule.windows.map((windowItem, windowIndex) => {
            const windowSteps = Array.isArray(windowItem?.contactSteps) && windowItem.contactSteps.length > 0
              ? windowItem.contactSteps
                  .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
                  .map((step, stepIndex) => ({
                    id: step.id || `step_${index}_${windowIndex}_${stepIndex}_${Math.random().toString(36).slice(2, 7)}`,
                    order: step.order || stepIndex + 1,
                    channel: step.channel || CHANNEL_OPTIONS[stepIndex]?.key || "telefono",
                    timingType: step.timingType || "immediate",
                    timingValue: step.timingValue || "",
                    fallbackChannel: normalizeFallbackChannel(step.fallbackChannel, "whatsapp"),
                  }))
              : contactSteps.map((step, stepIndex) => ({
                  ...step,
                  id: `step_${index}_${windowIndex}_${stepIndex}_${Math.random().toString(36).slice(2, 7)}`,
                }));
            return {
              id: windowItem.id || `window_${index}_${windowIndex}_${Math.random().toString(36).slice(2, 7)}`,
              from: windowItem.from || legacyTimeFrom,
              to: windowItem.to || legacyTimeTo,
              contactSteps: windowSteps,
            };
          })
        : null;
      const timeRanges = Array.isArray(rule?.timeRanges) && rule.timeRanges.length > 0
        ? rule.timeRanges.map((range) => ({
            id: range.id || `range_${index}_${Math.random().toString(36).slice(2, 7)}`,
            from: range.from || legacyTimeFrom,
            to: range.to || legacyTimeTo,
          }))
        : [buildLeadTimeRange(legacyTimeFrom, legacyTimeTo)];
      const windows = legacyWindows || timeRanges.map((range, rangeIndex) => ({
        id: `window_${index}_${rangeIndex}_${Math.random().toString(36).slice(2, 7)}`,
        from: range.from,
        to: range.to,
        contactSteps: contactSteps.map((step, stepIndex) => ({
          ...step,
          id: `step_${index}_${rangeIndex}_${stepIndex}_${Math.random().toString(36).slice(2, 7)}`,
        })),
      }));
      return {
        ...base,
        ...rule,
        days: Array.isArray(rule?.days) && rule.days.length > 0 ? rule.days : base.days,
        windows,
      };
    };

    return {
      ...cloneLeadProcessDefault(),
      ...parsed,
      commercialPolicy: {
        ...LEAD_PROCESS_DEFAULT.commercialPolicy,
        ...(parsed?.commercialPolicy || {}),
      },
      channels: {
        telefono: { ...LEAD_PROCESS_DEFAULT.channels.telefono, ...(parsed?.channels?.telefono || {}) },
        whatsapp: { ...LEAD_PROCESS_DEFAULT.channels.whatsapp, ...(parsed?.channels?.whatsapp || {}) },
        email: { ...LEAD_PROCESS_DEFAULT.channels.email, ...(parsed?.channels?.email || {}) },
      },
      agendaPolicy: {
        ...LEAD_PROCESS_DEFAULT.agendaPolicy,
        ...(parsed?.agendaPolicy || {}),
      },
      languagePolicy: {
        ...LEAD_PROCESS_DEFAULT.languagePolicy,
        ...(parsed?.languagePolicy || {}),
      },
      assignmentPolicy: {
        ...LEAD_PROCESS_DEFAULT.assignmentPolicy,
        ...(parsed?.assignmentPolicy || {}),
        existingClientMatchBy: Array.isArray(parsed?.assignmentPolicy?.existingClientMatchBy) ? parsed.assignmentPolicy.existingClientMatchBy : LEAD_PROCESS_DEFAULT.assignmentPolicy.existingClientMatchBy,
        excludeAgentsFromGeneralPool: Array.isArray(parsed?.assignmentPolicy?.excludeAgentsFromGeneralPool) ? parsed.assignmentPolicy.excludeAgentsFromGeneralPool : LEAD_PROCESS_DEFAULT.assignmentPolicy.excludeAgentsFromGeneralPool,
        directCaptorAgents: Array.isArray(parsed?.assignmentPolicy?.directCaptorAgents) ? parsed.assignmentPolicy.directCaptorAgents : LEAD_PROCESS_DEFAULT.assignmentPolicy.directCaptorAgents,
        localProvinceIds: Array.isArray(parsed?.assignmentPolicy?.localProvinceIds) ? parsed.assignmentPolicy.localProvinceIds : LEAD_PROCESS_DEFAULT.assignmentPolicy.localProvinceIds,
        metroProvinceIds: Array.isArray(parsed?.assignmentPolicy?.metroProvinceIds) ? parsed.assignmentPolicy.metroProvinceIds : LEAD_PROCESS_DEFAULT.assignmentPolicy.metroProvinceIds,
        metroValenciaLocalities: Array.isArray(parsed?.assignmentPolicy?.metroValenciaLocalities) ? parsed.assignmentPolicy.metroValenciaLocalities : LEAD_PROCESS_DEFAULT.assignmentPolicy.metroValenciaLocalities,
        metroAgents: Array.isArray(parsed?.assignmentPolicy?.metroAgents) ? parsed.assignmentPolicy.metroAgents : LEAD_PROCESS_DEFAULT.assignmentPolicy.metroAgents,
        remoteProvinceAgents: Array.isArray(parsed?.assignmentPolicy?.remoteProvinceAgents) ? parsed.assignmentPolicy.remoteProvinceAgents : LEAD_PROCESS_DEFAULT.assignmentPolicy.remoteProvinceAgents,
      },
      voicePolicy: {
        ...LEAD_PROCESS_DEFAULT.voicePolicy,
        ...(parsed?.voicePolicy || {}),
        realtimeRules: Array.isArray(parsed?.voicePolicy?.realtimeRules) ? parsed.voicePolicy.realtimeRules : LEAD_PROCESS_DEFAULT.voicePolicy.realtimeRules,
        callObjectiveInstructions: Array.isArray(parsed?.voicePolicy?.callObjectiveInstructions) ? parsed.voicePolicy.callObjectiveInstructions : LEAD_PROCESS_DEFAULT.voicePolicy.callObjectiveInstructions,
      },
      telemarketingPolicy: {
        ...LEAD_PROCESS_DEFAULT.telemarketingPolicy,
        ...(parsed?.telemarketingPolicy || {}),
        discardExamples: Array.isArray(parsed?.telemarketingPolicy?.discardExamples) ? parsed.telemarketingPolicy.discardExamples : LEAD_PROCESS_DEFAULT.telemarketingPolicy.discardExamples,
        propertyPresentationRules: Array.isArray(parsed?.telemarketingPolicy?.propertyPresentationRules) ? parsed.telemarketingPolicy.propertyPresentationRules : LEAD_PROCESS_DEFAULT.telemarketingPolicy.propertyPresentationRules,
        resultsState: {
          ...LEAD_PROCESS_DEFAULT.telemarketingPolicy.resultsState,
          ...(parsed?.telemarketingPolicy?.resultsState || {}),
        },
        guides: {
          ...LEAD_PROCESS_DEFAULT.telemarketingPolicy.guides,
          ...(parsed?.telemarketingPolicy?.guides || {}),
        },
      },
      responseRules: Array.isArray(parsed?.responseRules) && parsed.responseRules.length > 0
        ? parsed.responseRules.map((rule, index) => normalizeResponseRule(rule, index))
        : JSON.parse(JSON.stringify(LEAD_PROCESS_DEFAULT.responseRules)),
      agendaRules: Array.isArray(parsed?.agendaRules) && parsed.agendaRules.length > 0
        ? parsed.agendaRules
        : JSON.parse(JSON.stringify(LEAD_PROCESS_DEFAULT.agendaRules)),
      subphases: Array.isArray(parsed?.subphases) && parsed.subphases.length > 0
        ? parsed.subphases
        : JSON.parse(JSON.stringify(LEAD_PROCESS_DEFAULT.subphases)),
    };
  } catch {
    return cloneLeadProcessDefault();
  }
}

function buildEmptyWorkRule() {
  return {
    nombre: "",
    grupo_ui: "Atención de leads",
    proceso_clave: "atencion_leads",
    evento_clave: "lead_nuevo",
    prioridad: 100,
    activa: true,
    exclusiva: true,
    descripcion: "",
    condiciones: [],
    acciones: [],
  };
}

export default function Ana() {
  const { theme } = useTheme();
  const c = theme.colors;
  const { puede } = usePermiso();
  const veGlobal = puede("cfg_ana_global", "ver");
  const modGlobal = puede("cfg_ana_global", "modificar");
  const vePrivilegios = puede("cfg_ana_global", "ver") || puede("cfg_ana_procesos", "ver") || puede("cfg_ana_agentes_bloq", "ver") || puede("cfg_ana_contactos_bloq", "ver");
  const veReglasTrabajo = puede("cfg_ana_reglas", "ver");

  const [seccion, setSeccion] = useState("identidad");
  const [configuracion, setConfiguracion] = useState({});
  const [reglas, setReglas] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [privilegios, setPrivilegios] = useState(PRIVILEGIOS_VACIOS);
  const [workRules, setWorkRules] = useState([]);
  const [workRulesCatalogs, setWorkRulesCatalogs] = useState(WORK_RULES_DEFAULT);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [plantillaEditando, setPlantillaEditando] = useState(null);
  const [reglaEditando, setReglaEditando] = useState(null);
  const [workRuleEditando, setWorkRuleEditando] = useState(null);

  useEffect(() => { cargarTodo(); }, []);

  async function cargarPrivilegios(headers = null) {
    const authHeaders = headers || { Authorization: `Bearer ${localStorage.getItem("token")}` };
    const res = await axios.get(`${API}/ana/privilegios/resumen`, { headers: authHeaders });
    setPrivilegios(res.data.data || PRIVILEGIOS_VACIOS);
  }

  async function cargarWorkRules(headers = null) {
    const authHeaders = headers || { Authorization: `Bearer ${localStorage.getItem("token")}` };
    const [rulesRes, catalogsRes] = await Promise.all([
      axios.get(`${API}/ana/work-rules`, { headers: authHeaders }),
      axios.get(`${API}/ana/work-rules/catalogos`, { headers: authHeaders }),
    ]);
    setWorkRules(rulesRes.data.data || []);
    setWorkRulesCatalogs(catalogsRes.data.data || WORK_RULES_DEFAULT);
  }

  async function cargarTodo() {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const [cfg, reg, pla, priv, workRulesRes, workCatalogsRes] = await Promise.all([
        axios.get(`${API}/ana/configuracion`, { headers }),
        axios.get(`${API}/ana/reglas`, { headers }),
        axios.get(`${API}/ana/plantillas`, { headers }),
        axios.get(`${API}/ana/privilegios/resumen`, { headers }),
        axios.get(`${API}/ana/work-rules`, { headers }),
        axios.get(`${API}/ana/work-rules/catalogos`, { headers }),
      ]);
      const cfgMap = {};
      (cfg.data.data || []).forEach(item => { cfgMap[item.clave] = item; });
      setConfiguracion(cfgMap);
      setReglas(reg.data.data || []);
      setPlantillas(pla.data.data || []);
      setPrivilegios(priv.data.data || PRIVILEGIOS_VACIOS);
      setWorkRules(workRulesRes.data.data || []);
      setWorkRulesCatalogs(workCatalogsRes.data.data || WORK_RULES_DEFAULT);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function guardarConfig(clave, valor) {
    try {
      setGuardando(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      await axios.put(`${API}/ana/configuracion/${clave}`, { valor }, { headers });
      setConfiguracion(prev => ({ ...prev, [clave]: { ...prev[clave], valor } }));
      mostrarMensaje("Guardado correctamente", "ok");
    } catch (e) {
      mostrarMensaje("Error al guardar", "error");
    } finally { setGuardando(false); }
  }

  async function guardarRegla(regla) {
    try {
      setGuardando(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      await axios.put(`${API}/ana/reglas/${regla.id}`, regla, { headers });
      setReglas(prev => prev.map(r => r.id === regla.id ? regla : r));
      setReglaEditando(null);
      mostrarMensaje("Regla guardada", "ok");
    } catch (e) {
      mostrarMensaje("Error al guardar", "error");
    } finally { setGuardando(false); }
  }

  async function guardarPlantilla(plantilla) {
    try {
      setGuardando(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      await axios.put(`${API}/ana/plantillas/${plantilla.id}`, plantilla, { headers });
      setPlantillas(prev => prev.map(p => p.id === plantilla.id ? plantilla : p));
      setPlantillaEditando(null);
      mostrarMensaje("Plantilla guardada", "ok");
    } catch (e) {
      mostrarMensaje("Error al guardar", "error");
    } finally { setGuardando(false); }
  }

  async function guardarPrivilegioGlobal(payload) {
    try {
      setGuardando(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const res = await axios.put(`${API}/ana/privilegios/global`, payload, { headers });
      setPrivilegios(prev => ({ ...prev, global: res.data.data }));
      mostrarMensaje("Privilegios globales guardados", "ok");
    } catch (e) {
      mostrarMensaje("Error al guardar privilegios globales", "error");
    } finally { setGuardando(false); }
  }

  async function guardarPrivilegioProceso(procesoClave, payload) {
    try {
      setGuardando(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      await axios.put(`${API}/ana/privilegios/procesos/${procesoClave}`, payload, { headers });
      await cargarPrivilegios(headers);
      mostrarMensaje("Proceso actualizado", "ok");
    } catch (e) {
      mostrarMensaje("Error al guardar el proceso", "error");
    } finally { setGuardando(false); }
  }

  async function guardarPrivilegioAgente(agenteId, payload) {
    try {
      setGuardando(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      await axios.put(`${API}/ana/privilegios/agentes/${agenteId}`, payload, { headers });
      await cargarPrivilegios(headers);
      mostrarMensaje("Agente actualizado", "ok");
    } catch (e) {
      mostrarMensaje("Error al actualizar el agente", "error");
    } finally { setGuardando(false); }
  }

  async function crearPrivilegioContacto(payload) {
    try {
      setGuardando(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      await axios.post(`${API}/ana/privilegios/contactos`, payload, { headers });
      await cargarPrivilegios(headers);
      mostrarMensaje("Contacto bloqueado", "ok");
    } catch (e) {
      mostrarMensaje("Error al bloquear el contacto", "error");
    } finally { setGuardando(false); }
  }

  async function eliminarPrivilegioContacto(id) {
    try {
      if (!(await dialogs.confirm("¿Quitar este bloqueo de contacto?"))) return;
      setGuardando(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      await axios.delete(`${API}/ana/privilegios/contactos/${id}`, { headers });
      await cargarPrivilegios(headers);
      mostrarMensaje("Bloqueo eliminado", "ok");
    } catch (e) {
      mostrarMensaje("Error al eliminar el bloqueo", "error");
    } finally { setGuardando(false); }
  }

  async function guardarWorkRule(rule) {
    try {
      setGuardando(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      if (rule.id) {
        await axios.put(`${API}/ana/work-rules/${rule.id}`, rule, { headers });
      } else {
        await axios.post(`${API}/ana/work-rules`, rule, { headers });
      }
      await cargarWorkRules(headers);
      setWorkRuleEditando(null);
      mostrarMensaje("Regla de trabajo guardada", "ok");
    } catch (e) {
      mostrarMensaje("Error al guardar la regla de trabajo", "error");
    } finally { setGuardando(false); }
  }

  async function duplicarWorkRule(id) {
    try {
      setGuardando(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      await axios.post(`${API}/ana/work-rules/${id}/duplicar`, {}, { headers });
      await cargarWorkRules(headers);
      mostrarMensaje("Regla duplicada", "ok");
    } catch (e) {
      mostrarMensaje("Error al duplicar la regla", "error");
    } finally { setGuardando(false); }
  }

  async function eliminarWorkRule(id) {
    try {
      if (!(await dialogs.confirm("¿Eliminar esta regla de trabajo?"))) return;
      setGuardando(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      await axios.delete(`${API}/ana/work-rules/${id}`, { headers });
      await cargarWorkRules(headers);
      mostrarMensaje("Regla eliminada", "ok");
    } catch (e) {
      mostrarMensaje("Error al eliminar la regla", "error");
    } finally { setGuardando(false); }
  }

  function mostrarMensaje(texto, tipo) {
    setMensaje({ texto, tipo });
    setTimeout(() => setMensaje(null), 3000);
  }

  const cfgVal = (clave) => configuracion[clave]?.valor || "";

  const inputStyle = {
    width: "100%", background: c.bgInput, border: `1px solid ${c.sectionBorder}`,
    borderRadius: 8, padding: "8px 12px", color: c.textPrimary, fontSize: 14,
    boxSizing: "border-box", fontFamily: "inherit"
  };

  const secciones = [
    { id: "identidad", label: "🤖 Identidad y Prompt" },
    { id: "horario", label: "🕐 Horario y Comportamiento" },
    { id: "procesoLeads", label: "🧩 Atención de leads" },
    ...(vePrivilegios ? [{ id: "privilegios", label: "🔒 Privilegios" }] : []),
    ...(veReglasTrabajo ? [{ id: "reglasTrabajo", label: "🧭 Reglas de trabajo" }] : []),
    { id: "reglas", label: "⚡ Reglas por Evento (legacy)" },
    { id: "plantillas", label: "💬 Plantillas de Mensajes (legacy)" },
    { id: "traducciones", label: "🌍 Diccionario multiidioma" },
    { id: "whatsapp", label: "📱 WhatsApp de Ana" },
  ];

  // Si la sección activa queda oculta por permisos, volver a "identidad".
  useEffect(() => {
    if (seccion === "privilegios" && !vePrivilegios) setSeccion("identidad");
    if (seccion === "reglasTrabajo" && !veReglasTrabajo) setSeccion("identidad");
  }, [seccion, vePrivilegios, veReglasTrabajo]);

  // ── QR de Ana ────────────────────────────────────────────────
  const ANA_SESSION_ID = "f1f7f652-819e-43c8-9ba0-2f02afbc14a6";
  const [anaWaStatus, setAnaWaStatus]   = useState("not_started");
  const [anaWaQr, setAnaWaQr]           = useState(null);
  const [anaWaPhone, setAnaWaPhone]     = useState(null);
  const [anaWaAccion, setAnaWaAccion]   = useState(false);
  const [anaWaError, setAnaWaError]     = useState("");
  const [anaWaReconnecting, setAnaWaReconnecting] = useState(false);
  const [anaWaNeedsQrScan, setAnaWaNeedsQrScan]   = useState(false);
  const anaWaPollRef = useRef(null);

  useEffect(() => {
    if (seccion !== "whatsapp") {
      if (anaWaPollRef.current) clearInterval(anaWaPollRef.current);
      return;
    }
    const token = localStorage.getItem("token");
    const poll = async () => {
      try {
        const res  = await fetch(`${API}/whatsapp/estado-sesion/${ANA_SESSION_ID}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (!data.ok) return;
        setAnaWaStatus(data.data.status);
        setAnaWaQr(data.data.qrBase64);
        setAnaWaPhone(data.data.phone);
        setAnaWaReconnecting(!!data.data.reconnectingFromSaved);
        setAnaWaNeedsQrScan(!!data.data.needsQrScan);
        if (data.data.error) setAnaWaError(data.data.error);
        else if (data.data.status !== "disconnected") setAnaWaError("");
      } catch { /* ignorar */ }
    };
    poll();
    anaWaPollRef.current = setInterval(poll, 800);
    return () => clearInterval(anaWaPollRef.current);
  }, [seccion]);

  const handleAnaConectar = async () => {
    setAnaWaAccion(true);
    setAnaWaError("");
    try {
      const res  = await fetch(`${API}/whatsapp/conectar-sesion/${ANA_SESSION_ID}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await res.json();
      if (!data.ok) setAnaWaError(data.error || "Error al conectar");
      else setAnaWaStatus("connecting");
    } catch { setAnaWaError("No se pudo conectar con el servidor"); }
    setAnaWaAccion(false);
  };

  const handleAnaResetSesion = async () => {
    if (!(await dialogs.confirm("¿Eliminar la sesión guardada de Ana y generar un QR nuevo para escanear?"))) return;
    setAnaWaAccion(true);
    setAnaWaError("");
    setAnaWaReconnecting(false);
    setAnaWaNeedsQrScan(true);
    setAnaWaQr(null);
    setAnaWaPhone(null);
    setAnaWaStatus("connecting");
    try {
      // reiniciarSesionConQR ya hace wipe + QR limpio; no depender del DELETE previo
      // (si falla el borrado, antes no se llegaba a pedir el QR).
      const res = await fetch(`${API}/whatsapp/conectar-sesion/${ANA_SESSION_ID}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ forzar_qr: true }),
      });
      const data = await res.json();
      if (!data.ok) setAnaWaError(data.error || "Error al generar QR nuevo");
      else setAnaWaStatus("connecting");
    } catch {
      setAnaWaError("No se pudo conectar con el servidor");
    }
    setAnaWaAccion(false);
  };

  const handleAnaDesconectar = async () => {
    if (!(await dialogs.confirm("¿Desconectar el WhatsApp de Ana? Dejará de responder mensajes."))) return;
    setAnaWaAccion(true);
    try {
      const res  = await fetch(`${API}/whatsapp/desconectar-sesion/${ANA_SESSION_ID}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = await res.json();
      if (!data.ok) setAnaWaError(data.error || "Error al desconectar");
      else { setAnaWaStatus("not_started"); setAnaWaQr(null); setAnaWaPhone(null); }
    } catch { setAnaWaError("No se pudo conectar con el servidor"); }
    setAnaWaAccion(false);
  };

  const ACCIONES = ["responder", "transferir", "notificar", "ignorar", "esperar"];

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "calc(100vh - 56px)", color: c.textSecondary }}>
      Cargando configuración de Ana...
    </div>
  );

  return (
    <div style={{ height: "calc(100vh - 56px)", display: "flex", flexDirection: "column", background: c.bgMain, overflow: "hidden" }}>

      {/* HEADER */}
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${c.sectionBorder}`, background: c.bgPanel, display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ width: 44, height: 44, borderRadius: "50%", background: "linear-gradient(135deg, #7c3aed, #a855f7)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>🤖</div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: c.textPrimary }}>Configuración de Ana</div>
          <div style={{ fontSize: 13, color: c.textSecondary }}>Asesora Virtual IA de VitalCasa</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
          {mensaje && (
            <div style={{ padding: "6px 14px", borderRadius: 8, fontSize: 13, fontWeight: 500, background: mensaje.tipo === "ok" ? "#16a34a22" : "#ef444422", color: mensaje.tipo === "ok" ? "#16a34a" : "#ef4444", border: `1px solid ${mensaje.tipo === "ok" ? "#16a34a44" : "#ef444444"}` }}>
              {mensaje.tipo === "ok" ? "✓" : "✗"} {mensaje.texto}
            </div>
          )}
          {/* Toggle Ana activa */}
          {veGlobal && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13, color: c.textSecondary }}>Ana activa</span>
            <div
              onClick={() => modGlobal && guardarConfig("activa", cfgVal("activa") === "true" ? "false" : "true")}
              title={!modGlobal ? "No tienes permiso para activar/desactivar Ana" : undefined}
              style={{ width: 44, height: 24, borderRadius: 12, background: cfgVal("activa") === "true" ? "#7c3aed" : c.sectionBorder, cursor: modGlobal ? "pointer" : "not-allowed", position: "relative", transition: "background 0.2s", opacity: modGlobal ? 1 : 0.6 }}>
              <div style={{ position: "absolute", top: 2, left: cfgVal("activa") === "true" ? 22 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
            </div>
          </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>

        {/* SIDEBAR */}
        <div style={{ width: 220, borderRight: `1px solid ${c.sectionBorder}`, background: c.bgPanel, padding: "12px 8px", display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
          {secciones.map(s => (
            <button key={s.id} onClick={() => setSeccion(s.id)}
              style={{ textAlign: "left", padding: "10px 14px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: seccion === s.id ? 600 : 400, background: seccion === s.id ? "#7c3aed22" : "transparent", color: seccion === s.id ? "#a855f7" : c.textSecondary, transition: "all 0.15s" }}>
              {s.label}
            </button>
          ))}
        </div>

        {/* CONTENIDO */}
        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>

          {/* SECCION: IDENTIDAD Y PROMPT */}
          {seccion === "identidad" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 800 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Identidad general</div>
                <div style={{ fontSize: 13, color: c.textSecondary }}>La lógica comercial multicanal de leads se configura en Atención de leads. Aquí solo queda la identidad general histórica.</div>
              </div>

              {/* Nombre */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: c.textSecondary, display: "block", marginBottom: 6 }}>NOMBRE DE LA ASESORA</label>
                <input value={cfgVal("nombre")} onChange={e => setConfiguracion(prev => ({ ...prev, nombre: { ...prev.nombre, valor: e.target.value } }))}
                  onBlur={e => guardarConfig("nombre", e.target.value)} style={{ ...inputStyle, maxWidth: 300 }} />
              </div>

              {/* Idiomas */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: c.textSecondary, display: "block", marginBottom: 6 }}>IDIOMAS</label>
                <input value={cfgVal("idiomas")} onChange={e => setConfiguracion(prev => ({ ...prev, idiomas: { ...prev.idiomas, valor: e.target.value } }))}
                  onBlur={e => guardarConfig("idiomas", e.target.value)} style={inputStyle} placeholder="español, inglés, alemán, francés..." />
                <div style={{ fontSize: 11, color: c.textSecondary, marginTop: 4 }}>Separados por comas. Ana detectará el idioma del cliente y responderá en el mismo.</div>
              </div>

              {/* Prompt principal legacy */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: c.textSecondary, display: "block", marginBottom: 6 }}>PROMPT PRINCIPAL LEGACY</label>
                <div style={{ fontSize: 12, color: c.textSecondary, marginBottom: 8 }}>Ya no gobierna la Atención de leads. Se muestra solo como referencia histórica para evitar contradicciones con la configuración activa.</div>
                <textarea
                  value={cfgVal("prompt_principal")}
                  rows={12}
                  readOnly
                  style={{ ...inputStyle, resize: "vertical", lineHeight: 1.6, opacity: 0.7 }}
                  placeholder="Prompt legacy"
                />
                <div style={{ fontSize: 11, color: c.textSecondary, marginTop: 4 }}>La fuente activa para teléfono, WhatsApp, email y agenda está en `Atención de leads`.</div>
              </div>
            </div>
          )}

          {/* SECCION: HORARIO Y COMPORTAMIENTO */}
          {seccion === "horario" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 600 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Horario y Comportamiento</div>
                <div style={{ fontSize: 13, color: c.textSecondary }}>Define cuándo atiende Ana y cómo gestiona el seguimiento.</div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: c.textSecondary, display: "block", marginBottom: 6 }}>HORA DE INICIO</label>
                  <input type="time" value={cfgVal("horario_inicio")}
                    onChange={e => setConfiguracion(prev => ({ ...prev, horario_inicio: { ...prev.horario_inicio, valor: e.target.value } }))}
                    onBlur={e => guardarConfig("horario_inicio", e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: c.textSecondary, display: "block", marginBottom: 6 }}>HORA DE FIN</label>
                  <input type="time" value={cfgVal("horario_fin")}
                    onChange={e => setConfiguracion(prev => ({ ...prev, horario_fin: { ...prev.horario_fin, valor: e.target.value } }))}
                    onBlur={e => guardarConfig("horario_fin", e.target.value)} style={inputStyle} />
                </div>
              </div>

              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: c.textSecondary, display: "block", marginBottom: 6 }}>DÍAS DE ATENCIÓN</label>
                <input value={cfgVal("dias_atencion")}
                  onChange={e => setConfiguracion(prev => ({ ...prev, dias_atencion: { ...prev.dias_atencion, valor: e.target.value } }))}
                  onBlur={e => guardarConfig("dias_atencion", e.target.value)} style={inputStyle} placeholder="lunes,martes,miércoles..." />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: c.textSecondary, display: "block", marginBottom: 6 }}>TIEMPO MÁXIMO RESPUESTA (min)</label>
                  <input type="number" value={cfgVal("tiempo_respuesta_max")}
                    onChange={e => setConfiguracion(prev => ({ ...prev, tiempo_respuesta_max: { ...prev.tiempo_respuesta_max, valor: e.target.value } }))}
                    onBlur={e => guardarConfig("tiempo_respuesta_max", e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: c.textSecondary, display: "block", marginBottom: 6 }}>REINTENTOS SEGUIMIENTO</label>
                  <input type="number" value={cfgVal("reintentos_seguimiento")}
                    onChange={e => setConfiguracion(prev => ({ ...prev, reintentos_seguimiento: { ...prev.reintentos_seguimiento, valor: e.target.value } }))}
                    onBlur={e => guardarConfig("reintentos_seguimiento", e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: c.textSecondary, display: "block", marginBottom: 6 }}>HORAS ENTRE REINTENTOS</label>
                  <input type="number" value={cfgVal("horas_entre_reintentos")}
                    onChange={e => setConfiguracion(prev => ({ ...prev, horas_entre_reintentos: { ...prev.horas_entre_reintentos, valor: e.target.value } }))}
                    onBlur={e => guardarConfig("horas_entre_reintentos", e.target.value)} style={inputStyle} />
                </div>
              </div>
            </div>
          )}

          {seccion === "privilegios" && (
            <SeccionPrivilegios
              c={c}
              inputStyle={inputStyle}
              privilegios={privilegios}
              guardando={guardando}
              onGuardarGlobal={guardarPrivilegioGlobal}
              onGuardarProceso={guardarPrivilegioProceso}
              onGuardarAgente={guardarPrivilegioAgente}
              onCrearContacto={crearPrivilegioContacto}
              onEliminarContacto={eliminarPrivilegioContacto}
              puede={puede}
            />
          )}

          {seccion === "procesoLeads" && (
            <SeccionProcesoLeads
              c={c}
              inputStyle={inputStyle}
              valor={cfgVal("lead_attention_process")}
              guardando={guardando}
              onGuardar={(valor) => guardarConfig("lead_attention_process", valor)}
              puede={puede}
            />
          )}

          {seccion === "reglasTrabajo" && (
            <SeccionWorkRules
              c={c}
              inputStyle={inputStyle}
              workRules={workRules}
              catalogs={workRulesCatalogs}
              guardando={guardando}
              editando={workRuleEditando}
              onEditar={setWorkRuleEditando}
              onCancelar={() => setWorkRuleEditando(null)}
              onGuardar={guardarWorkRule}
              onDuplicar={duplicarWorkRule}
              onEliminar={eliminarWorkRule}
              puede={puede}
            />
          )}

          {/* SECCION: REGLAS POR EVENTO */}
          {seccion === "reglas" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 800 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Reglas por Evento (legacy)</div>
                <div style={{ fontSize: 13, color: c.textSecondary }}>Esta sección ya no gobierna la atención activa de leads. Se mantiene solo como referencia histórica del sistema anterior.</div>
              </div>

              {reglas.map(regla => (
                <div key={regla.id} style={{ background: c.bgPanel, border: `1px solid ${c.sectionBorder}`, borderRadius: 10, padding: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: regla.activa ? "#16a34a" : "#94a3b8", flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: c.textPrimary }}>{regla.descripcion}</div>
                      <div style={{ fontSize: 12, color: c.textSecondary, marginTop: 2 }}>Evento: <span style={{ color: "#a855f7" }}>{regla.evento}</span> · Acción histórica: <span style={{ color: c.textPrimary, fontWeight: 500 }}>{regla.accion}</span></div>
                    </div>
                    <span style={{ fontSize: 11, color: c.textSecondary, border: `1px solid ${c.sectionBorder}`, borderRadius: 999, padding: "4px 10px" }}>Solo lectura</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* SECCION: PLANTILLAS */}
          {seccion === "plantillas" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 800 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Plantillas de Mensajes (legacy)</div>
                <div style={{ fontSize: 13, color: c.textSecondary }}>Mensajes predefinidos que Ana usa en cada situación. Usa {"{{nombre}}"}, {"{{fecha}}"}, {"{{hora}}"} como variables.</div>
                <div style={{ fontSize: 12, color: c.textSecondary, marginTop: 8 }}>La atención de leads multicanal ya no toma autoridad de estas plantillas; se conservan solo como referencia histórica y no modifican el runtime actual.</div>
              </div>

              {plantillas.map(plantilla => (
                <div key={plantilla.id} style={{ background: c.bgPanel, border: `1px solid ${c.sectionBorder}`, borderRadius: 10, padding: 16 }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: plantilla.activa ? "#16a34a" : "#94a3b8", flexShrink: 0 }} />
                      <div style={{ fontSize: 13, fontWeight: 600, color: c.textPrimary, flex: 1 }}>{plantilla.nombre}</div>
                      <span style={{ fontSize: 11, background: "#7c3aed22", color: "#a855f7", borderRadius: 4, padding: "2px 8px" }}>{plantilla.clave}</span>
                      <span style={{ fontSize: 11, color: c.textSecondary, border: `1px solid ${c.sectionBorder}`, borderRadius: 999, padding: "4px 10px" }}>Solo lectura</span>
                    </div>
                    <div style={{ fontSize: 13, color: c.textSecondary, background: c.bgInput, borderRadius: 8, padding: "10px 12px", lineHeight: 1.6 }}>{plantilla.contenido}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

{/* SECCION: DICCIONARIO MULTIIDIOMA */}
          {seccion === "traducciones" && (
            <SeccionTraducciones c={c} inputStyle={inputStyle} API={API} mostrarMensaje={mostrarMensaje} />
          )}

        {/* SECCION: WHATSAPP DE ANA */}
          {seccion === "whatsapp" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 600 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>WhatsApp de Ana</div>
                <div style={{ fontSize: 13, color: c.textSecondary }}>Conecta el teléfono físico de Ana para que pueda recibir y responder mensajes automáticamente.</div>
              </div>

              {/* Tarjeta estado */}
              <div style={{ background: c.bgPanel, border: `1px solid ${c.sectionBorder}`, borderRadius: 12, overflow: "hidden" }}>
                <div style={{ height: 3, background: "linear-gradient(90deg,#22c55e,#16a34a,#15803d,#16a34a,#22c55e)" }} />
                <div style={{ padding: "16px 20px", borderBottom: `1px solid ${c.sectionBorder}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary, marginBottom: 6 }}>Estado de la sesión de Ana</div>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 12px", borderRadius: 20,
                      background: anaWaStatus === "connected" ? "rgba(22,163,74,0.1)" : anaWaStatus === "qr_ready" || anaWaStatus === "connecting" ? "rgba(245,158,11,0.1)" : "rgba(239,68,68,0.1)",
                      border: `1px solid ${anaWaStatus === "connected" ? "rgba(22,163,74,0.3)" : anaWaStatus === "qr_ready" || anaWaStatus === "connecting" ? "rgba(245,158,11,0.3)" : "rgba(239,68,68,0.3)"}`,
                    }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
                        background: anaWaStatus === "connected" ? "#16a34a" : anaWaStatus === "qr_ready" || anaWaStatus === "connecting" ? "#f59e0b" : "#ef4444",
                        animation: ["connecting","qr_ready"].includes(anaWaStatus) ? "wa-pulse 1.4s ease-in-out infinite" : "none",
                      }} />
                      <span style={{ fontSize: 12, fontWeight: 600,
                        color: anaWaStatus === "connected" ? "#16a34a" : anaWaStatus === "qr_ready" || anaWaStatus === "connecting" ? "#f59e0b" : "#ef4444",
                      }}>
                        {anaWaStatus === "connected" ? `Conectada · +${anaWaPhone}` : anaWaQr ? "Escanea el QR" : anaWaReconnecting || anaWaStatus === "saved" ? "Reconectando sesión guardada…" : anaWaStatus === "qr_ready" ? "Esperando escaneo del QR" : anaWaStatus === "connecting" ? "Conectando..." : "Sin conexión"}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {anaWaStatus === "connected" && (
                      <button onClick={handleAnaDesconectar} disabled={anaWaAccion} style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.08)", color: "#ef4444", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                        Desconectar
                      </button>
                    )}
                    <button onClick={handleAnaResetSesion} disabled={anaWaAccion} style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid rgba(245,158,11,0.3)", background: "rgba(245,158,11,0.08)", color: "#f59e0b", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                      🔄 Reiniciar sesión
                    </button>
                  </div>
                </div>

                <div style={{ padding: "24px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
                  {anaWaStatus === "connected" && (
                    <>
                      <div style={{ fontSize: 48 }}>✅</div>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#16a34a", marginBottom: 4 }}>Ana está conectada</div>
                        <div style={{ fontSize: 13, color: c.textSecondary }}>Está recibiendo y respondiendo mensajes desde <strong style={{ color: c.textPrimary }}>+{anaWaPhone}</strong></div>
                      </div>
                    </>
                  )}
                  {anaWaQr && (
                    <>
                      <div style={{ fontSize: 14, fontWeight: 600, color: c.textPrimary }}>
                        {anaWaNeedsQrScan
                          ? "La sesión guardada no reconectó sola — escanea este QR con el móvil de Ana"
                          : "Escanea con el teléfono de Ana"}
                      </div>
                      <div style={{ padding: 12, borderRadius: 12, background: "#fff", border: "2px solid rgba(22,163,74,0.25)", boxShadow: "0 8px 32px rgba(22,163,74,0.2)" }}>
                        <img src={anaWaQr} alt="QR Ana" style={{ width: 220, height: 220, display: "block" }} />
                      </div>
                      <div style={{ fontSize: 12, color: c.textSecondary, textAlign: "center" }}>
                        WhatsApp → Dispositivos vinculados → Vincular dispositivo<br />
                        El código se renueva solo; déjalo en pantalla hasta escanear.
                      </div>
                    </>
                  )}
                  {!anaWaQr && (anaWaStatus === "connecting" || anaWaStatus === "saved") && (
                    <>
                      <div style={{ width: 40, height: 40, borderRadius: "50%", border: `3px solid ${c.sectionBorder}`, borderTop: "3px solid #16a34a", animation: "wa-spin 0.9s linear infinite" }} />
                      <div style={{ fontSize: 13, color: c.textSecondary, textAlign: "center" }}>
                        {anaWaReconnecting || anaWaStatus === "saved"
                          ? "Reconectando sesión guardada… Si en ~1 minuto no aparece el QR, pulsa «Reiniciar sesión»."
                          : "Generando código QR..."}
                      </div>
                      {(anaWaReconnecting || anaWaStatus === "saved") && (
                        <button
                          onClick={handleAnaResetSesion}
                          disabled={anaWaAccion}
                          style={{
                            padding: "10px 20px",
                            borderRadius: 10,
                            border: "none",
                            background: "linear-gradient(135deg,#b45309,#d97706)",
                            color: "#fffbeb",
                            cursor: "pointer",
                            fontFamily: "inherit",
                            fontSize: 13,
                            fontWeight: 700,
                            opacity: anaWaAccion ? 0.7 : 1,
                          }}
                        >
                          Generar QR nuevo ahora
                        </button>
                      )}
                    </>
                  )}
                  {(anaWaStatus === "not_started" || anaWaStatus === "disconnected") && (
                    <>
                      <div style={{ fontSize: 13, color: c.textSecondary, textAlign: "center" }}>
                        {anaWaStatus === "disconnected" ? "La sesión de Ana se cerró. Vuelve a conectarla." : "Ana aún no tiene WhatsApp conectado."}
                      </div>
                      <button onClick={handleAnaConectar} disabled={anaWaAccion} style={{ padding: "11px 28px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#15803d,#16a34a)", color: "#bbf7d0", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 8, opacity: anaWaAccion ? 0.7 : 1 }}>
                        <span>📱</span> {anaWaAccion ? "Iniciando..." : "Conectar WhatsApp de Ana"}
                      </button>
                    </>
                  )}
                  {anaWaError && (
                    <div style={{ padding: "8px 14px", borderRadius: 8, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", fontSize: 12, color: "#ef4444", fontWeight: 600 }}>⚠️ {anaWaError}</div>
                  )}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function SeccionWorkRules({
  c,
  inputStyle,
  workRules,
  catalogs,
  guardando,
  editando,
  onEditar,
  onCancelar,
  onGuardar,
  onDuplicar,
  onEliminar,
  puede,
}) {
  const altaReglas = puede("cfg_ana_reglas", "alta");
  const modReglas = puede("cfg_ana_reglas", "modificar");
  const bajaReglas = puede("cfg_ana_reglas", "baja");
  const disabledTitle = "No tienes permiso para modificar las reglas de trabajo";

  const cardStyle = {
    background: c.bgPanel,
    border: `1px solid ${c.sectionBorder}`,
    borderRadius: 12,
    padding: 16,
  };

  const rule = editando || buildEmptyWorkRule();
  const puedeEditarForm = rule.id ? modReglas : altaReglas;

  function updateRule(patch) {
    onEditar({ ...rule, ...patch });
  }

  function updateCondition(index, patch) {
    const next = [...(rule.condiciones || [])];
    next[index] = { ...next[index], ...patch };
    updateRule({ condiciones: next });
  }

  function updateAction(index, patch) {
    const next = [...(rule.acciones || [])];
    next[index] = { ...next[index], ...patch };
    updateRule({ acciones: next });
  }

  function removeCondition(index) {
    updateRule({ condiciones: (rule.condiciones || []).filter((_, i) => i !== index) });
  }

  function removeAction(index) {
    updateRule({ acciones: (rule.acciones || []).filter((_, i) => i !== index) });
  }

  const grupos = catalogs.grupos || [];
  const procesos = catalogs.procesos || [];
  const eventos = catalogs.eventos || [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 980 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Reglas de trabajo</div>
          <div style={{ fontSize: 13, color: c.textSecondary }}>
            Define cuándo actúa Ana, con qué proceso y qué secuencia debe ejecutar.
          </div>
        </div>
        {!editando && altaReglas && (
          <button
            onClick={() => onEditar(buildEmptyWorkRule())}
            style={{ background: "linear-gradient(135deg, #7c3aed, #a855f7)", color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
          >
            + Nueva regla
          </button>
        )}
      </div>

      {editando && (
        <div style={cardStyle}>
          <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary, marginBottom: 12 }}>
            {rule.id ? "Editar regla" : "Nueva regla"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>NOMBRE</label>
              <input value={rule.nombre} onChange={(e) => updateRule({ nombre: e.target.value })} style={inputStyle} disabled={!puedeEditarForm} title={!puedeEditarForm ? disabledTitle : undefined} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>GRUPO</label>
              <select value={rule.grupo_ui} onChange={(e) => updateRule({ grupo_ui: e.target.value })} style={inputStyle} disabled={!puedeEditarForm} title={!puedeEditarForm ? disabledTitle : undefined}>
                {grupos.map((grupo) => <option key={grupo} value={grupo}>{grupo}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>PRIORIDAD</label>
              <input type="number" value={rule.prioridad} onChange={(e) => updateRule({ prioridad: Number(e.target.value) || 0 })} style={inputStyle} disabled={!puedeEditarForm} title={!puedeEditarForm ? disabledTitle : undefined} />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>PROCESO</label>
              <select value={rule.proceso_clave} onChange={(e) => updateRule({ proceso_clave: e.target.value })} style={inputStyle} disabled={!puedeEditarForm} title={!puedeEditarForm ? disabledTitle : undefined}>
                {procesos.map((proceso) => <option key={proceso.proceso_clave} value={proceso.proceso_clave}>{proceso.nombre}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>EVENTO</label>
              <select value={rule.evento_clave} onChange={(e) => updateRule({ evento_clave: e.target.value })} style={inputStyle} disabled={!puedeEditarForm} title={!puedeEditarForm ? disabledTitle : undefined}>
                {eventos.map((evento) => <option key={evento.evento_clave} value={evento.evento_clave}>{evento.nombre}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>DESCRIPCIÓN</label>
            <textarea value={rule.descripcion || ""} onChange={(e) => updateRule({ descripcion: e.target.value })} rows={3} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }} disabled={!puedeEditarForm} title={!puedeEditarForm ? disabledTitle : undefined} />
          </div>

          <div style={{ display: "flex", gap: 20, marginBottom: 16 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: c.textPrimary }}>
              <input type="checkbox" checked={!!rule.activa} onChange={(e) => updateRule({ activa: e.target.checked })} disabled={!puedeEditarForm} />
              Regla activa
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: c.textPrimary }}>
              <input type="checkbox" checked={!!rule.exclusiva} onChange={(e) => updateRule({ exclusiva: e.target.checked })} disabled={!puedeEditarForm} />
              Exclusiva
            </label>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary }}>Condiciones</div>
              <button
                onClick={() => puedeEditarForm && updateRule({ condiciones: [...(rule.condiciones || []), { field: "weekday", operator: "in", value: "" }] })}
                disabled={!puedeEditarForm} title={!puedeEditarForm ? disabledTitle : undefined}
                style={{ background: c.bgInput, border: `1px solid ${c.sectionBorder}`, color: c.textPrimary, borderRadius: 8, padding: "6px 12px", cursor: puedeEditarForm ? "pointer" : "not-allowed", fontSize: 12, opacity: puedeEditarForm ? 1 : 0.5 }}
              >
                + Añadir condición
              </button>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {(rule.condiciones || []).length === 0 ? (
                <div style={{ fontSize: 12, color: c.textSecondary }}>Sin condiciones: la regla aplica siempre que coincidan proceso y evento.</div>
              ) : (
                (rule.condiciones || []).map((cond, index) => (
                  <div key={index} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.2fr auto", gap: 8 }}>
                    <select value={cond.field || ""} onChange={(e) => updateCondition(index, { field: e.target.value })} style={inputStyle} disabled={!puedeEditarForm}>
                      {(catalogs.campos_condicion || []).map((field) => <option key={field} value={field}>{field}</option>)}
                    </select>
                    <select value={cond.operator || ""} onChange={(e) => updateCondition(index, { operator: e.target.value })} style={inputStyle} disabled={!puedeEditarForm}>
                      {(catalogs.operadores || []).map((operator) => <option key={operator} value={operator}>{operator}</option>)}
                    </select>
                    <input value={cond.value ?? ""} onChange={(e) => updateCondition(index, { value: e.target.value })} style={inputStyle} placeholder="Valor" disabled={!puedeEditarForm} />
                    <button onClick={() => removeCondition(index)} disabled={!puedeEditarForm} title={!puedeEditarForm ? disabledTitle : undefined} style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#ef4444", borderRadius: 8, padding: "6px 12px", cursor: puedeEditarForm ? "pointer" : "not-allowed", fontSize: 12, opacity: puedeEditarForm ? 1 : 0.5 }}>Quitar</button>
                  </div>
                ))
              )}
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary }}>Acciones</div>
              <button
                onClick={() => puedeEditarForm && updateRule({ acciones: [...(rule.acciones || []), { action_type: "call_now", delay_type: "none", delay_value: "", payload: "" }] })}
                disabled={!puedeEditarForm} title={!puedeEditarForm ? disabledTitle : undefined}
                style={{ background: c.bgInput, border: `1px solid ${c.sectionBorder}`, color: c.textPrimary, borderRadius: 8, padding: "6px 12px", cursor: puedeEditarForm ? "pointer" : "not-allowed", fontSize: 12, opacity: puedeEditarForm ? 1 : 0.5 }}
              >
                + Añadir acción
              </button>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {(rule.acciones || []).map((action, index) => (
                <div key={index} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1.2fr auto", gap: 8 }}>
                  <select value={action.action_type || ""} onChange={(e) => updateAction(index, { action_type: e.target.value })} style={inputStyle} disabled={!puedeEditarForm}>
                    {(catalogs.tipos_accion || []).map((type) => <option key={type} value={type}>{type}</option>)}
                  </select>
                  <select value={action.delay_type || ""} onChange={(e) => updateAction(index, { delay_type: e.target.value })} style={inputStyle} disabled={!puedeEditarForm}>
                    {(catalogs.tipos_delay || []).map((type) => <option key={type} value={type}>{type}</option>)}
                  </select>
                  <input value={action.delay_value ?? ""} onChange={(e) => updateAction(index, { delay_value: e.target.value })} style={inputStyle} placeholder="Delay" disabled={!puedeEditarForm} />
                  <input value={action.payload ?? ""} onChange={(e) => updateAction(index, { payload: e.target.value })} style={inputStyle} placeholder="Payload opcional" disabled={!puedeEditarForm} />
                  <button onClick={() => removeAction(index)} disabled={!puedeEditarForm} title={!puedeEditarForm ? disabledTitle : undefined} style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#ef4444", borderRadius: 8, padding: "6px 12px", cursor: puedeEditarForm ? "pointer" : "not-allowed", fontSize: 12, opacity: puedeEditarForm ? 1 : 0.5 }}>Quitar</button>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <button onClick={onCancelar} style={{ background: "none", border: `1px solid ${c.sectionBorder}`, color: c.textSecondary, borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontSize: 13 }}>Cancelar</button>
            <button onClick={() => onGuardar(rule)} disabled={guardando || !rule.nombre || !puedeEditarForm} title={!puedeEditarForm ? disabledTitle : undefined} style={{ background: "linear-gradient(135deg, #7c3aed, #a855f7)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Guardar regla</button>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gap: 12 }}>
        {(workRules || []).length === 0 ? (
          <div style={cardStyle}>
            <div style={{ fontSize: 13, color: c.textSecondary }}>Aún no hay reglas de trabajo creadas.</div>
          </div>
        ) : (
          (workRules || []).map((ruleItem) => (
            <div key={ruleItem.id} style={cardStyle}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary }}>{ruleItem.nombre}</div>
                  <div style={{ fontSize: 12, color: c.textSecondary, marginTop: 4 }}>
                    {ruleItem.grupo_ui} · {ruleItem.proceso_clave} · {ruleItem.evento_clave} · prioridad {ruleItem.prioridad}
                  </div>
                  {ruleItem.descripcion && (
                    <div style={{ fontSize: 12, color: c.textSecondary, marginTop: 6 }}>{ruleItem.descripcion}</div>
                  )}
                  <div style={{ fontSize: 12, color: c.textSecondary, marginTop: 8 }}>
                    Condiciones: {(ruleItem.condiciones || []).length} · Acciones: {(ruleItem.acciones || []).length} · {ruleItem.activa ? "Activa" : "Inactiva"} · {ruleItem.exclusiva ? "Exclusiva" : "Acumulable"}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => onEditar({ ...ruleItem })} disabled={!modReglas} title={!modReglas ? disabledTitle : undefined} style={{ background: c.bgInput, border: `1px solid ${c.sectionBorder}`, color: c.textPrimary, borderRadius: 8, padding: "6px 12px", cursor: modReglas ? "pointer" : "not-allowed", fontSize: 12, opacity: modReglas ? 1 : 0.5 }}>Editar</button>
                  {altaReglas && (
                  <button onClick={() => onDuplicar(ruleItem.id)} style={{ background: c.bgInput, border: `1px solid ${c.sectionBorder}`, color: c.textPrimary, borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12 }}>Duplicar</button>
                  )}
                  <button onClick={() => onEliminar(ruleItem.id)} disabled={!bajaReglas} title={!bajaReglas ? "No tienes permiso para eliminar reglas de trabajo" : undefined} style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#ef4444", borderRadius: 8, padding: "6px 12px", cursor: bajaReglas ? "pointer" : "not-allowed", fontSize: 12, opacity: bajaReglas ? 1 : 0.5 }}>Eliminar</button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function SeccionPrivilegios({
  c,
  inputStyle,
  privilegios,
  guardando,
  onGuardarGlobal,
  onGuardarProceso,
  onGuardarAgente,
  onCrearContacto,
  onEliminarContacto,
  puede,
}) {
  const veGlobal = puede("cfg_ana_global", "ver");
  const modGlobal = puede("cfg_ana_global", "modificar");
  const veProcesos = puede("cfg_ana_procesos", "ver");
  const modProcesos = puede("cfg_ana_procesos", "modificar");
  const veAgentesBloq = puede("cfg_ana_agentes_bloq", "ver");
  const modAgentesBloq = puede("cfg_ana_agentes_bloq", "modificar");
  const veContactosBloq = puede("cfg_ana_contactos_bloq", "ver");
  const modContactosBloq = puede("cfg_ana_contactos_bloq", "modificar");
  const [motivoGlobal, setMotivoGlobal] = useState(privilegios.global?.motivo || "");
  const [motivosProcesos, setMotivosProcesos] = useState({});
  const [nuevoAgenteId, setNuevoAgenteId] = useState("");
  const [motivoAgente, setMotivoAgente] = useState("");
  const [nuevoContacto, setNuevoContacto] = useState({
    tipo_contacto: "contacto_generico",
    nombre_contacto: "",
    telefono: "",
    email: "",
    motivo: "",
    origen_bloqueo: "manual",
  });

  useEffect(() => {
    setMotivoGlobal(privilegios.global?.motivo || "");
  }, [privilegios.global?.motivo]);

  useEffect(() => {
    const next = {};
    (privilegios.procesos || []).forEach((proceso) => {
      next[proceso.proceso_clave] = proceso.motivo || "";
    });
    setMotivosProcesos(next);
  }, [privilegios.procesos]);

  const cardStyle = {
    background: c.bgPanel,
    border: `1px solid ${c.sectionBorder}`,
    borderRadius: 12,
    padding: 16,
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 980 }}>
      <div>
        <div style={{ fontSize: 16, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Privilegios de Ana</div>
        <div style={{ fontSize: 13, color: c.textSecondary }}>
          Controla dónde puede actuar Ana y dónde debe quedar completamente bloqueada.
        </div>
      </div>

      {veGlobal && (
      <div style={cardStyle}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary }}>Bloqueo global</div>
            <div style={{ fontSize: 12, color: c.textSecondary }}>Si desactivas este interruptor, Ana no actuará en ningún proceso.</div>
          </div>
          <div
            onClick={() => modGlobal && onGuardarGlobal({
              ana_activa_global: !(privilegios.global?.ana_activa_global ?? true),
              motivo: motivoGlobal,
            })}
            title={!modGlobal ? "No tienes permiso para activar/desactivar Ana globalmente" : undefined}
            style={{
              width: 48,
              height: 26,
              borderRadius: 20,
              background: (privilegios.global?.ana_activa_global ?? true) ? "#16a34a" : "#ef4444",
              cursor: modGlobal ? "pointer" : "not-allowed",
              opacity: modGlobal ? 1 : 0.6,
              position: "relative",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 3,
                left: (privilegios.global?.ana_activa_global ?? true) ? 24 : 3,
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: "#fff",
                transition: "left 0.2s",
              }}
            />
          </div>
        </div>
        <label style={{ fontSize: 12, color: c.textSecondary, display: "block", marginBottom: 6 }}>MOTIVO / NOTA INTERNA</label>
        <textarea
          value={motivoGlobal}
          onChange={(e) => setMotivoGlobal(e.target.value)}
          rows={3}
          style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }}
          placeholder="Ej: pausa general mientras revisamos el comportamiento de Ana"
          disabled={!modGlobal}
          title={!modGlobal ? "No tienes permiso para activar/desactivar Ana globalmente" : undefined}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
          <button
            onClick={() => onGuardarGlobal({
              ana_activa_global: privilegios.global?.ana_activa_global ?? true,
              motivo: motivoGlobal,
            })}
            disabled={guardando || !modGlobal}
            title={!modGlobal ? "No tienes permiso para activar/desactivar Ana globalmente" : undefined}
            style={{ background: "linear-gradient(135deg, #7c3aed, #a855f7)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
          >
            Guardar bloqueo global
          </button>
        </div>
      </div>
      )}

      {veProcesos && (
      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Bloqueos por proceso</div>
        <div style={{ fontSize: 12, color: c.textSecondary, marginBottom: 12 }}>
          Activa o desactiva cada proceso sin tocar código.
        </div>
        <div style={{ display: "grid", gap: 12 }}>
          {(privilegios.procesos || []).map((proceso) => (
            <div key={proceso.proceso_clave} style={{ border: `1px solid ${c.sectionBorder}`, borderRadius: 10, padding: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary }}>{proceso.nombre}</div>
                  <div style={{ fontSize: 11, color: c.textSecondary }}>{proceso.grupo_ui} · {proceso.proceso_clave}</div>
                </div>
                <div
                  onClick={() => modProcesos && onGuardarProceso(proceso.proceso_clave, {
                    activa: !proceso.activa,
                    motivo: motivosProcesos[proceso.proceso_clave] || "",
                  })}
                  title={!modProcesos ? "No tienes permiso para modificar privilegios por proceso" : undefined}
                  style={{
                    width: 44,
                    height: 24,
                    borderRadius: 14,
                    background: proceso.activa ? "#16a34a" : "#ef4444",
                    cursor: modProcesos ? "pointer" : "not-allowed",
                    opacity: modProcesos ? 1 : 0.6,
                    position: "relative",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 2,
                      left: proceso.activa ? 22 : 2,
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      background: "#fff",
                    }}
                  />
                </div>
              </div>
              <input
                value={motivosProcesos[proceso.proceso_clave] || ""}
                onChange={(e) => setMotivosProcesos((prev) => ({ ...prev, [proceso.proceso_clave]: e.target.value }))}
                placeholder="Motivo opcional"
                style={inputStyle}
                disabled={!modProcesos}
                title={!modProcesos ? "No tienes permiso para modificar privilegios por proceso" : undefined}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                <button
                  onClick={() => onGuardarProceso(proceso.proceso_clave, {
                    activa: proceso.activa,
                    motivo: motivosProcesos[proceso.proceso_clave] || "",
                  })}
                  disabled={guardando || !modProcesos}
                  title={!modProcesos ? "No tienes permiso para modificar privilegios por proceso" : undefined}
                  style={{ background: c.bgInput, border: `1px solid ${c.sectionBorder}`, color: c.textPrimary, borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 12 }}
                >
                  Guardar
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      )}

      {veAgentesBloq && (
      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Bloqueos por agente</div>
        <div style={{ fontSize: 12, color: c.textSecondary, marginBottom: 12 }}>
          Útil si un agente no quiere ayuda de Ana en ninguno de sus procesos.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr auto", gap: 10, alignItems: "end", marginBottom: 14 }}>
          <div>
            <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>AGENTE</label>
            <select value={nuevoAgenteId} onChange={(e) => setNuevoAgenteId(e.target.value)} style={inputStyle} disabled={!modAgentesBloq}>
              <option value="">Selecciona un agente...</option>
              {(privilegios.agentes_disponibles || []).map((agente) => (
                <option key={agente.id} value={agente.id}>{agente.nombre}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>MOTIVO</label>
            <input value={motivoAgente} onChange={(e) => setMotivoAgente(e.target.value)} style={inputStyle} disabled={!modAgentesBloq} />
          </div>
          <button
            onClick={() => {
              if (!nuevoAgenteId) return;
              onGuardarAgente(nuevoAgenteId, { bloqueada: true, motivo: motivoAgente });
              setNuevoAgenteId("");
              setMotivoAgente("");
            }}
            disabled={guardando || !nuevoAgenteId || !modAgentesBloq}
            title={!modAgentesBloq ? "No tienes permiso para modificar agentes bloqueados" : undefined}
            style={{ background: "linear-gradient(135deg, #7c3aed, #a855f7)", color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
          >
            Bloquear agente
          </button>
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          {(privilegios.agentes_bloqueados || []).length === 0 ? (
            <div style={{ fontSize: 13, color: c.textSecondary }}>No hay agentes bloqueados.</div>
          ) : (
            (privilegios.agentes_bloqueados || []).map((agente) => (
              <div key={agente.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", border: `1px solid ${c.sectionBorder}`, borderRadius: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary }}>{agente.agente_nombre || agente.agente_id}</div>
                  <div style={{ fontSize: 12, color: c.textSecondary }}>{agente.motivo || "Sin motivo"}</div>
                </div>
                <button
                  onClick={() => onGuardarAgente(agente.agente_id, { bloqueada: false, motivo: agente.motivo || "" })}
                  disabled={guardando || !modAgentesBloq}
                  title={!modAgentesBloq ? "No tienes permiso para modificar agentes bloqueados" : undefined}
                  style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#ef4444", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12 }}
                >
                  Quitar bloqueo
                </button>
              </div>
            ))
          )}
        </div>
      </div>
      )}

      {veContactosBloq && (
      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Bloqueos por contacto</div>
        <div style={{ fontSize: 12, color: c.textSecondary, marginBottom: 12 }}>
          Para clientes o leads que solo deben ser tratados por un humano.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "160px 1fr 1fr", gap: 10, marginBottom: 10 }}>
          <div>
            <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>TIPO</label>
            <select
              value={nuevoContacto.tipo_contacto}
              onChange={(e) => setNuevoContacto((prev) => ({ ...prev, tipo_contacto: e.target.value }))}
              style={inputStyle}
              disabled={!modContactosBloq}
            >
              <option value="contacto_generico">Contacto genérico</option>
              <option value="lead">Lead</option>
              <option value="cliente">Cliente</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>NOMBRE</label>
            <input
              value={nuevoContacto.nombre_contacto}
              onChange={(e) => setNuevoContacto((prev) => ({ ...prev, nombre_contacto: e.target.value }))}
              style={inputStyle}
              disabled={!modContactosBloq}
            />
          </div>
          <div>
            <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>MOTIVO</label>
            <input
              value={nuevoContacto.motivo}
              onChange={(e) => setNuevoContacto((prev) => ({ ...prev, motivo: e.target.value }))}
              style={inputStyle}
              disabled={!modContactosBloq}
            />
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, alignItems: "end", marginBottom: 14 }}>
          <div>
            <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>TELÉFONO</label>
            <input
              value={nuevoContacto.telefono}
              onChange={(e) => setNuevoContacto((prev) => ({ ...prev, telefono: e.target.value }))}
              style={inputStyle}
              disabled={!modContactosBloq}
            />
          </div>
          <div>
            <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>EMAIL</label>
            <input
              value={nuevoContacto.email}
              onChange={(e) => setNuevoContacto((prev) => ({ ...prev, email: e.target.value }))}
              style={inputStyle}
              disabled={!modContactosBloq}
            />
          </div>
          <button
            onClick={() => {
              if (!nuevoContacto.telefono && !nuevoContacto.email) return;
              onCrearContacto(nuevoContacto);
              setNuevoContacto({
                tipo_contacto: "contacto_generico",
                nombre_contacto: "",
                telefono: "",
                email: "",
                motivo: "",
                origen_bloqueo: "manual",
              });
            }}
            disabled={guardando || (!nuevoContacto.telefono && !nuevoContacto.email) || !modContactosBloq}
            title={!modContactosBloq ? "No tienes permiso para modificar contactos bloqueados" : undefined}
            style={{ background: "linear-gradient(135deg, #7c3aed, #a855f7)", color: "#fff", border: "none", borderRadius: 8, padding: "10px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
          >
            Bloquear contacto
          </button>
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          {(privilegios.contactos_bloqueados || []).length === 0 ? (
            <div style={{ fontSize: 13, color: c.textSecondary }}>No hay contactos bloqueados.</div>
          ) : (
            (privilegios.contactos_bloqueados || []).map((contacto) => (
              <div key={contacto.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", border: `1px solid ${c.sectionBorder}`, borderRadius: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary }}>
                    {contacto.nombre_contacto || contacto.telefono || contacto.email || contacto.id}
                  </div>
                  <div style={{ fontSize: 12, color: c.textSecondary }}>
                    {[contacto.tipo_contacto, contacto.telefono, contacto.email].filter(Boolean).join(" · ")}
                  </div>
                  <div style={{ fontSize: 12, color: c.textSecondary }}>{contacto.motivo || "Sin motivo"}</div>
                </div>
                <button
                  onClick={() => onEliminarContacto(contacto.id)}
                  disabled={guardando || !modContactosBloq}
                  title={!modContactosBloq ? "No tienes permiso para modificar contactos bloqueados" : undefined}
                  style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#ef4444", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12 }}
                >
                  Quitar bloqueo
                </button>
              </div>
            ))
          )}
        </div>
      </div>
      )}
    </div>
  );
}

function SeccionProcesoLeads({ c, inputStyle, valor, guardando, onGuardar, puede }) {
  const veHorario = puede("cfg_ana_horario", "ver");
  const modHorario = puede("cfg_ana_horario", "modificar");
  const veCanales = puede("cfg_ana_canales", "ver");
  const modCanales = puede("cfg_ana_canales", "modificar");
  const veTiming = puede("cfg_ana_timing", "ver");
  const modTiming = puede("cfg_ana_timing", "modificar");
  const veAgendaSlot = puede("cfg_ana_agenda_slot", "ver");
  const modAgendaSlot = puede("cfg_ana_agenda_slot", "modificar");
  const veRespuestaLead = veHorario || veCanales || veTiming;
  const modAlgunaRespuestaLead = modHorario || modCanales || modTiming;
  const [config, setConfig] = useState(() => parseLeadProcessConfig(valor));

  useEffect(() => {
    setConfig(parseLeadProcessConfig(valor));
  }, [valor]);

  const cardStyle = {
    background: c.bgPanel,
    border: `1px solid ${c.sectionBorder}`,
    borderRadius: 12,
    padding: 16,
  };

  function updateChannel(channel, patch) {
    setConfig((prev) => ({
      ...prev,
      channels: {
        ...prev.channels,
        [channel]: { ...prev.channels[channel], ...patch },
      },
    }));
  }

  function toggleSubphaseChannel(subphaseKey, channel) {
    setConfig((prev) => ({
      ...prev,
      subphases: prev.subphases.map((subphase) => {
        if (subphase.key !== subphaseKey) return subphase;
        const next = new Set(subphase.allowedChannels || []);
        if (next.has(channel)) next.delete(channel);
        else next.add(channel);
        return { ...subphase, allowedChannels: [...next] };
      }),
    }));
  }

  function updateResponseRule(ruleId, patch) {
    setConfig((prev) => ({
      ...prev,
      responseRules: (prev.responseRules || []).map((rule) => rule.id === ruleId ? { ...rule, ...patch } : rule),
    }));
  }

  function toggleResponseRuleDay(ruleId, day) {
    setConfig((prev) => ({
      ...prev,
      responseRules: (prev.responseRules || []).map((rule) => {
        if (rule.id !== ruleId) return rule;
        const next = new Set(rule.days || []);
        if (next.has(day)) next.delete(day);
        else next.add(day);
        return { ...rule, days: [...next] };
      }),
    }));
  }

  function addResponseRule() {
    setConfig((prev) => ({ ...prev, responseRules: [...(prev.responseRules || []), buildLeadResponseRule()] }));
  }

  function removeResponseRule(ruleId) {
    setConfig((prev) => ({
      ...prev,
      responseRules: (prev.responseRules || []).filter((rule) => rule.id !== ruleId),
    }));
  }

  function addResponseRuleRange(ruleId) {
    setConfig((prev) => ({
      ...prev,
      responseRules: (prev.responseRules || []).map((rule) =>
        rule.id === ruleId
          ? { ...rule, windows: [...(rule.windows || []), buildLeadResponseWindow("00:00", "23:59")] }
          : rule
      ),
    }));
  }

  function updateResponseRuleRange(ruleId, windowId, patch) {
    setConfig((prev) => ({
      ...prev,
      responseRules: (prev.responseRules || []).map((rule) =>
        rule.id === ruleId
          ? {
              ...rule,
              windows: (rule.windows || []).map((windowItem) =>
                windowItem.id === windowId ? { ...windowItem, ...patch } : windowItem
              ),
            }
          : rule
      ),
    }));
  }

  function removeResponseRuleRange(ruleId, windowId) {
    setConfig((prev) => ({
      ...prev,
      responseRules: (prev.responseRules || []).map((rule) => {
        if (rule.id !== ruleId) return rule;
        const nextRanges = (rule.windows || []).filter((windowItem) => windowItem.id !== windowId);
        return {
          ...rule,
          windows: nextRanges.length > 0 ? nextRanges : [buildLeadResponseWindow("00:00", "23:59")],
        };
      }),
    }));
  }

  function updateResponseRuleStep(ruleId, windowId, stepId, patch) {
    setConfig((prev) => ({
      ...prev,
      responseRules: (prev.responseRules || []).map((rule) =>
        rule.id === ruleId
          ? {
              ...rule,
              windows: (rule.windows || []).map((windowItem) =>
                windowItem.id === windowId
                  ? {
                      ...windowItem,
                      contactSteps: (windowItem.contactSteps || []).map((step) =>
                        step.id === stepId ? { ...step, ...patch } : step
                      ),
                    }
                  : windowItem
              ),
            }
          : rule
      ),
    }));
  }

  function addResponseRuleStep(ruleId, windowId) {
    setConfig((prev) => ({
      ...prev,
      responseRules: (prev.responseRules || []).map((rule) =>
        rule.id === ruleId
          ? {
              ...rule,
              windows: (rule.windows || []).map((windowItem) => {
                if (windowItem.id !== windowId) return windowItem;
                const nextOrder = (windowItem.contactSteps || []).length + 1;
                return {
                  ...windowItem,
                  contactSteps: [
                    ...(windowItem.contactSteps || []),
                    buildLeadContactStep(nextOrder, "telefono", "immediate", "", "whatsapp"),
                  ],
                };
              }),
            }
          : rule
      ),
    }));
  }

  function removeResponseRuleStep(ruleId, windowId, stepId) {
    setConfig((prev) => ({
      ...prev,
      responseRules: (prev.responseRules || []).map((rule) =>
        rule.id === ruleId
          ? {
              ...rule,
              windows: (rule.windows || []).map((windowItem) => {
                if (windowItem.id !== windowId) return windowItem;
                const remaining = (windowItem.contactSteps || []).filter((step) => step.id !== stepId);
                const normalized = remaining.map((step, index) => ({ ...step, order: index + 1 }));
                return {
                  ...windowItem,
                  contactSteps: normalized.length > 0
                    ? normalized
                    : [buildLeadContactStep(1, "telefono", "immediate", "", "whatsapp")],
                };
              }),
            }
          : rule
      ),
    }));
  }

  function updateAgendaRule(ruleId, patch) {
    setConfig((prev) => ({
      ...prev,
      agendaRules: (prev.agendaRules || []).map((rule) => rule.id === ruleId ? { ...rule, ...patch } : rule),
    }));
  }

  function toggleAgendaRuleDay(ruleId, day) {
    setConfig((prev) => ({
      ...prev,
      agendaRules: (prev.agendaRules || []).map((rule) => {
        if (rule.id !== ruleId) return rule;
        const next = new Set(rule.days || []);
        if (next.has(day)) next.delete(day);
        else next.add(day);
        return { ...rule, days: [...next] };
      }),
    }));
  }

  function addAgendaRule() {
    setConfig((prev) => ({ ...prev, agendaRules: [...(prev.agendaRules || []), buildLeadAgendaRule()] }));
  }

  function removeAgendaRule(ruleId) {
    setConfig((prev) => ({
      ...prev,
      agendaRules: (prev.agendaRules || []).filter((rule) => rule.id !== ruleId),
    }));
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 980 }}>
      <div>
        <div style={{ fontSize: 16, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Macroproceso único de Atención de leads</div>
        <div style={{ fontSize: 13, color: c.textSecondary }}>
          Aquí se centraliza la política común de teléfono, WhatsApp y email: objetivo comercial, subfases y reglas de agenda.
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ display: "grid", gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: c.textSecondary, display: "block", marginBottom: 6 }}>NOMBRE DEL MACROPROCESO</label>
            <input value={config.nombre} onChange={(e) => setConfig((prev) => ({ ...prev, nombre: e.target.value }))} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: c.textSecondary, display: "block", marginBottom: 6 }}>DESCRIPCIÓN</label>
            <textarea value={config.descripcion} onChange={(e) => setConfig((prev) => ({ ...prev, descripcion: e.target.value }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: c.textSecondary, display: "block", marginBottom: 6 }}>MÁXIMO DE DUDAS ANTES DE OFRECER VISITA</label>
              <input
                type="number"
                min="1"
                value={config.commercialPolicy.maxInformativeAnswersBeforeVisit}
                onChange={(e) => setConfig((prev) => ({
                  ...prev,
                  commercialPolicy: {
                    ...prev.commercialPolicy,
                    maxInformativeAnswersBeforeVisit: Number(e.target.value || 2),
                  },
                }))}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: c.textSecondary, display: "block", marginBottom: 6 }}>ESTILO DE APERTURA</label>
              <select
                value={config.commercialPolicy.openingStyle}
                onChange={(e) => setConfig((prev) => ({
                  ...prev,
                  commercialPolicy: { ...prev.commercialPolicy, openingStyle: e.target.value },
                }))}
                style={inputStyle}
              >
                <option value="specific_property_first">Ir primero a la propiedad/duda concreta</option>
                <option value="contextual_intro">Presentación breve con contexto</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Canales</div>
        <div style={{ fontSize: 12, color: c.textSecondary, marginBottom: 12 }}>
          Activa cada canal dentro del mismo macroproceso, en lugar de mantener cerebros separados.
        </div>
        <div style={{ display: "grid", gap: 12 }}>
          {Object.entries(config.channels).map(([channel, channelConfig]) => (
            <div key={channel} style={{ border: `1px solid ${c.sectionBorder}`, borderRadius: 10, padding: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary, textTransform: "capitalize" }}>{channel}</div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: c.textSecondary }}>
                  <input type="checkbox" checked={channelConfig.enabled !== false} onChange={(e) => updateChannel(channel, { enabled: e.target.checked })} />
                  Habilitado
                </label>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>TONO</label>
                  <input value={channelConfig.tone || ""} onChange={(e) => updateChannel(channel, { tone: e.target.value })} style={inputStyle} />
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: c.textSecondary, alignSelf: "end" }}>
                  <input type="checkbox" checked={channelConfig.firstContact === true} onChange={(e) => updateChannel(channel, { firstContact: e.target.checked })} />
                  Disponible para primer contacto
                </label>
              </div>
              <div>
                <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>NOTAS DE ESTILO</label>
                <textarea value={channelConfig.styleNotes || ""} onChange={(e) => updateChannel(channel, { styleNotes: e.target.value })} rows={2} style={{ ...inputStyle, resize: "vertical" }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Subfases internas</div>
        <div style={{ fontSize: 12, color: c.textSecondary, marginBottom: 12 }}>
          Define el objetivo de cada subfase y por qué canales puede ejecutarse.
        </div>
        <div style={{ display: "grid", gap: 12 }}>
          {config.subphases.map((subphase) => (
            <div key={subphase.key} style={{ border: `1px solid ${c.sectionBorder}`, borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: c.textPrimary, marginBottom: 8 }}>{subphase.label}</div>
              <textarea
                value={subphase.objective || ""}
                onChange={(e) => setConfig((prev) => ({
                  ...prev,
                  subphases: prev.subphases.map((item) => item.key === subphase.key ? { ...item, objective: e.target.value } : item),
                }))}
                rows={2}
                style={{ ...inputStyle, resize: "vertical", marginBottom: 10 }}
              />
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {["telefono", "whatsapp", "email"].map((channel) => (
                  <label key={channel} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: c.textSecondary }}>
                    <input
                      type="checkbox"
                      checked={(subphase.allowedChannels || []).includes(channel)}
                      onChange={() => toggleSubphaseChannel(subphase.key, channel)}
                    />
                    {channel}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Política de agenda</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: c.textSecondary }}>
            <input
              type="checkbox"
              checked={config.agendaPolicy.useConcreteSlots === true}
              onChange={(e) => setConfig((prev) => ({ ...prev, agendaPolicy: { ...prev.agendaPolicy, useConcreteSlots: e.target.checked } }))}
            />
            Proponer huecos concretos
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: c.textSecondary }}>
            <input
              type="checkbox"
              checked={config.agendaPolicy.proposeAvailabilityWindow === true}
              onChange={(e) => setConfig((prev) => ({ ...prev, agendaPolicy: { ...prev.agendaPolicy, proposeAvailabilityWindow: e.target.checked } }))}
            />
            Permitir disponibilidad abierta
          </label>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
          <textarea value={config.agendaPolicy.offerConcreteSlotsInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, agendaPolicy: { ...prev.agendaPolicy, offerConcreteSlotsInstruction: e.target.value } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder="Cómo ofrecer huecos concretos" />
          <textarea value={config.agendaPolicy.secondOptionInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, agendaPolicy: { ...prev.agendaPolicy, secondOptionInstruction: e.target.value } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder="Cómo pasar a la opción 2" />
          <textarea value={config.agendaPolicy.rejectBothInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, agendaPolicy: { ...prev.agendaPolicy, rejectBothInstruction: e.target.value } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder="Si rechaza las dos opciones" />
          <textarea value={config.agendaPolicy.exactDayInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, agendaPolicy: { ...prev.agendaPolicy, exactDayInstruction: e.target.value } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder="Cómo decir el día exacto" />
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Instrucciones comerciales comunes</div>
        <div style={{ fontSize: 12, color: c.textSecondary, marginBottom: 12 }}>
          Aquí va el criterio comercial compartido por teléfono, WhatsApp y email.
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          <div>
            <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>TRAS LA PRIMERA DUDA</label>
            <textarea value={config.commercialPolicy.firstFollowUpInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, commercialPolicy: { ...prev.commercialPolicy, firstFollowUpInstruction: e.target.value } }))} rows={2} style={{ ...inputStyle, resize: "vertical" }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>CUANDO YA HA RESUELTO UNA DUDA</label>
            <textarea value={config.commercialPolicy.followUpAfterFirstAnswerInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, commercialPolicy: { ...prev.commercialPolicy, followUpAfterFirstAnswerInstruction: e.target.value } }))} rows={2} style={{ ...inputStyle, resize: "vertical" }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>GIRO A VISITA</label>
            <textarea value={config.commercialPolicy.visitPivotInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, commercialPolicy: { ...prev.commercialPolicy, visitPivotInstruction: e.target.value } }))} rows={2} style={{ ...inputStyle, resize: "vertical" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>SEÑALES DE CIERRE PASIVO (UNA POR LÍNEA)</label>
              <textarea value={(config.commercialPolicy.passiveCloseSignals || []).join("\n")} onChange={(e) => setConfig((prev) => ({ ...prev, commercialPolicy: { ...prev.commercialPolicy, passiveCloseSignals: splitTextareaLines(e.target.value) } }))} rows={4} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>REGLA SI EL CLIENTE INTENTA CERRAR</label>
              <textarea value={config.commercialPolicy.passiveCloseInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, commercialPolicy: { ...prev.commercialPolicy, passiveCloseInstruction: e.target.value } }))} rows={4} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>PROHIBICIÓN DE CIERRE PASIVO</label>
              <textarea value={config.commercialPolicy.passiveCloseProhibition || ""} onChange={(e) => setConfig((prev) => ({ ...prev, commercialPolicy: { ...prev.commercialPolicy, passiveCloseProhibition: e.target.value } }))} rows={2} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>SI DESCARTA LA PROPIEDAD</label>
              <textarea value={config.commercialPolicy.discardToTelemarketingInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, commercialPolicy: { ...prev.commercialPolicy, discardToTelemarketingInstruction: e.target.value } }))} rows={2} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Idioma</div>
        <div style={{ fontSize: 12, color: c.textSecondary, marginBottom: 12 }}>
          Regla común para todos los canales: usar el idioma del lead o del cliente y dejar español por defecto si no hay ninguna pista.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: c.textSecondary }}>
            <input type="checkbox" checked={config.languagePolicy.useLeadLanguageWhenAvailable === true} onChange={(e) => setConfig((prev) => ({ ...prev, languagePolicy: { ...prev.languagePolicy, useLeadLanguageWhenAvailable: e.target.checked } }))} />
            Empezar por el idioma que venga en el lead
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: c.textSecondary }}>
            <input type="checkbox" checked={config.languagePolicy.mirrorClientLanguage === true} onChange={(e) => setConfig((prev) => ({ ...prev, languagePolicy: { ...prev.languagePolicy, mirrorClientLanguage: e.target.checked } }))} />
            Cambiar al idioma del cliente si habla/escribe en otro
          </label>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <input value={config.languagePolicy.defaultLanguage || ""} onChange={(e) => setConfig((prev) => ({ ...prev, languagePolicy: { ...prev.languagePolicy, defaultLanguage: e.target.value } }))} style={inputStyle} placeholder="Idioma por defecto" />
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: c.textSecondary }}>
            <input type="checkbox" checked={config.languagePolicy.fallbackToDefaultWhenUnknown === true} onChange={(e) => setConfig((prev) => ({ ...prev, languagePolicy: { ...prev.languagePolicy, fallbackToDefaultWhenUnknown: e.target.checked } }))} />
            Si no hay pistas, volver al idioma por defecto
          </label>
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          <textarea value={config.languagePolicy.sameLanguageInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, languagePolicy: { ...prev.languagePolicy, sameLanguageInstruction: e.target.value } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder="Regla principal de idioma" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <textarea value={config.languagePolicy.defaultLanguageInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, languagePolicy: { ...prev.languagePolicy, defaultLanguageInstruction: e.target.value } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder="Si no hay señal de idioma" />
            <textarea value={config.languagePolicy.dontMixLanguagesInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, languagePolicy: { ...prev.languagePolicy, dontMixLanguagesInstruction: e.target.value } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder="No mezclar idiomas" />
            <textarea value={config.languagePolicy.unsupportedLanguageInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, languagePolicy: { ...prev.languagePolicy, unsupportedLanguageInstruction: e.target.value } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder="Si el idioma no está soportado" />
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Asignación de leads</div>
        <div style={{ fontSize: 12, color: c.textSecondary, marginBottom: 12 }}>
          Define cómo se reparte un lead nuevo entre las comerciales: idioma, cliente existente, exclusivas, captador, provincias y carga.
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: c.textSecondary }}>
              <input type="checkbox" checked={config.assignmentPolicy.enabled === true} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentPolicy: { ...prev.assignmentPolicy, enabled: e.target.checked } }))} />
              Asignación automática activa
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: c.textSecondary }}>
              <input type="checkbox" checked={config.assignmentPolicy.prioritizeLeadLanguage === true} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentPolicy: { ...prev.assignmentPolicy, prioritizeLeadLanguage: e.target.checked } }))} />
              Priorizar idioma del lead
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: c.textSecondary }}>
              <input type="checkbox" checked={config.assignmentPolicy.keepExistingClientWithSameAgent === true} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentPolicy: { ...prev.assignmentPolicy, keepExistingClientWithSameAgent: e.target.checked } }))} />
              Mantener mismo agente en cliente existente
            </label>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: c.textSecondary }}>
              <input type="checkbox" checked={config.assignmentPolicy.prioritizeExclusivePropertyCaptor === true} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentPolicy: { ...prev.assignmentPolicy, prioritizeExclusivePropertyCaptor: e.target.checked } }))} />
              Exclusiva al captador
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: c.textSecondary }}>
              <input type="checkbox" checked={config.assignmentPolicy.prioritizeCaptorWhenAvailable === true} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentPolicy: { ...prev.assignmentPolicy, prioritizeCaptorWhenAvailable: e.target.checked } }))} />
              Priorizar captador si está equilibrado
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: c.textSecondary }}>
              <input type="checkbox" checked={config.assignmentPolicy.respectAgentMaxLeads === true} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentPolicy: { ...prev.assignmentPolicy, respectAgentMaxLeads: e.target.checked } }))} />
              Respetar máximo de leads por agente
            </label>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>IDIOMA PUENTE</label>
              <input value={config.assignmentPolicy.bridgeLanguage || ""} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentPolicy: { ...prev.assignmentPolicy, bridgeLanguage: e.target.value } }))} style={inputStyle} placeholder="en" />
            </div>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>DIFERENCIA MÁXIMA PARA RESPETAR CAPTADOR</label>
              <input type="number" value={config.assignmentPolicy.captorPriorityMaxLeadGap ?? 10} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentPolicy: { ...prev.assignmentPolicy, captorPriorityMaxLeadGap: Number(e.target.value || 10) } }))} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>ESTRATEGIA FINAL</label>
              <input value={config.assignmentPolicy.fallbackStrategy || ""} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentPolicy: { ...prev.assignmentPolicy, fallbackStrategy: e.target.value } }))} style={inputStyle} placeholder="least_loaded" />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>MATCH CLIENTE EXISTENTE (UNO POR LÍNEA)</label>
              <textarea value={(config.assignmentPolicy.existingClientMatchBy || []).join("\n")} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentPolicy: { ...prev.assignmentPolicy, existingClientMatchBy: splitTextareaLines(e.target.value) } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>EXCLUIR DEL REPARTO GENERAL (UNO POR LÍNEA)</label>
              <textarea value={(config.assignmentPolicy.excludeAgentsFromGeneralPool || []).join("\n")} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentPolicy: { ...prev.assignmentPolicy, excludeAgentsFromGeneralPool: splitTextareaLines(e.target.value) } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>ASIGNACIÓN DIRECTA SI CAPTAN (UNO POR LÍNEA)</label>
              <textarea value={(config.assignmentPolicy.directCaptorAgents || []).join("\n")} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentPolicy: { ...prev.assignmentPolicy, directCaptorAgents: splitTextareaLines(e.target.value) } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>PROVINCIAS LOCALES (UNA POR LÍNEA)</label>
              <textarea value={(config.assignmentPolicy.localProvinceIds || []).join("\n")} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentPolicy: { ...prev.assignmentPolicy, localProvinceIds: splitTextareaLines(e.target.value).map((item) => Number(item)).filter(Number.isFinite) } }))} rows={4} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>PROVINCIAS GRANDES CIUDADES (UNA POR LÍNEA)</label>
              <textarea value={(config.assignmentPolicy.metroProvinceIds || []).join("\n")} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentPolicy: { ...prev.assignmentPolicy, metroProvinceIds: splitTextareaLines(e.target.value).map((item) => Number(item)).filter(Number.isFinite) } }))} rows={4} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>PROVINCIA DE VALENCIA CAPITAL</label>
              <input type="number" value={config.assignmentPolicy.metroValenciaProvinceId ?? 46} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentPolicy: { ...prev.assignmentPolicy, metroValenciaProvinceId: Number(e.target.value || 46) } }))} style={inputStyle} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>LOCALIDADES VALENCIA CAPITAL (UNA POR LÍNEA)</label>
              <textarea value={(config.assignmentPolicy.metroValenciaLocalities || []).join("\n")} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentPolicy: { ...prev.assignmentPolicy, metroValenciaLocalities: splitTextareaLines(e.target.value) } }))} rows={4} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>AGENTES GRANDES CIUDADES (UNO POR LÍNEA)</label>
              <textarea value={(config.assignmentPolicy.metroAgents || []).join("\n")} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentPolicy: { ...prev.assignmentPolicy, metroAgents: splitTextareaLines(e.target.value) } }))} rows={4} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>AGENTES PROVINCIA LEJANA (UNO POR LÍNEA)</label>
              <textarea value={(config.assignmentPolicy.remoteProvinceAgents || []).join("\n")} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentPolicy: { ...prev.assignmentPolicy, remoteProvinceAgents: splitTextareaLines(e.target.value) } }))} rows={4} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            <div style={{ fontSize: 12, color: c.textSecondary, lineHeight: 1.5, padding: "8px 0" }}>
              Orden actual:
              {"\n"}1. Idioma
              {"\n"}2. Cliente existente
              {"\n"}3. Exclusiva
              {"\n"}4. Exclusiones / asignación directa
              {"\n"}5. Captador
              {"\n"}6. Provincia
              {"\n"}7. Menor carga
            </div>
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Voz y apertura</div>
        <div style={{ fontSize: 12, color: c.textSecondary, marginBottom: 12 }}>
          Refuerzo específico de llamadas de voz y primera frase al descolgar.
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>REGLAS REALTIME (UNA POR LÍNEA)</label>
              <textarea value={(config.voicePolicy.realtimeRules || []).join("\n")} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, realtimeRules: splitTextareaLines(e.target.value) } }))} rows={6} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>OBJETIVO DE LA LLAMADA (UNA REGLA POR LÍNEA)</label>
              <textarea value={(config.voicePolicy.callObjectiveInstructions || []).join("\n")} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, callObjectiveInstructions: splitTextareaLines(e.target.value) } }))} rows={6} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>APERTURA SI YA CONOCEMOS AL CLIENTE</label>
              <textarea value={config.voicePolicy.knownLeadOpeningInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, knownLeadOpeningInstruction: e.target.value } }))} rows={4} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>APERTURA SI ES LEAD NUEVO</label>
              <textarea value={config.voicePolicy.newLeadOpeningInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, newLeadOpeningInstruction: e.target.value } }))} rows={4} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <textarea value={config.voicePolicy.outboundChannelInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, outboundChannelInstruction: e.target.value } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder="Habla tú primero al descolgar" />
            <textarea value={config.voicePolicy.outboundStyleInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, outboundStyleInstruction: e.target.value } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder="Estilo oral" />
            <textarea value={config.voicePolicy.avoidRepeatLeadDataInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, avoidRepeatLeadDataInstruction: e.target.value } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder="No repetir datos del lead" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <textarea value={config.voicePolicy.avoidPassiveCloseInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, avoidPassiveCloseInstruction: e.target.value } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder="No cerrar pasivamente" />
            <textarea value={config.voicePolicy.avoidGenericOpeningInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, avoidGenericOpeningInstruction: e.target.value } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder="Evitar aperturas genéricas" />
            <textarea value={config.voicePolicy.openingGenerationInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, openingGenerationInstruction: e.target.value } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder="Cómo debe sonar la primera frase" />
          </div>
          <div style={{ border: `1px solid ${c.sectionBorder}`, borderRadius: 10, padding: 12, background: c.bgInput }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Flujo ejecutable de voz</div>
            <div style={{ fontSize: 12, color: c.textSecondary, marginBottom: 12 }}>
              Estos campos gobiernan el runtime de llamadas outbound en `realtime`: silencios, salto a visita, oferta de huecos concretos y arranque de T2.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: c.textPrimary }}>
                <input
                  type="checkbox"
                  checked={config.voicePolicy.useStateMachineForOutboundCalls !== false}
                  onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, useStateMachineForOutboundCalls: e.target.checked } }))}
                />
                Usar state machine en llamadas outbound
              </label>
              <div>
                <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>SILENCIO DE FIN DE TURNO (ms)</label>
                <input type="number" min="300" step="50" value={config.voicePolicy.turnSilenceMs ?? 750} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, turnSilenceMs: Number(e.target.value || 0) } }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>ESPERA ANTES DE REPREGUNTAR (s)</label>
                <input type="number" min="1" step="1" value={config.voicePolicy.userSilenceTimeoutSeconds ?? 4} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, userSilenceTimeoutSeconds: Number(e.target.value || 0) } }))} style={inputStyle} />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <textarea value={config.voicePolicy.askAnotherQuestionPrompt || ""} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, askAnotherQuestionPrompt: e.target.value } }))} rows={2} style={{ ...inputStyle, resize: "vertical" }} placeholder="Repregunta breve tras resolver la primera duda" />
              <textarea value={config.voicePolicy.visitOfferPrompt || ""} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, visitOfferPrompt: e.target.value } }))} rows={2} style={{ ...inputStyle, resize: "vertical" }} placeholder="Frase de giro a visita" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <textarea value={config.voicePolicy.concreteSlotOfferPrompt || ""} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, concreteSlotOfferPrompt: e.target.value } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder="Plantilla para ofrecer huecos concretos" />
              <textarea value={config.voicePolicy.concreteSlotSecondOptionClause || ""} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, concreteSlotSecondOptionClause: e.target.value } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder="Cláusula opcional para la segunda opción" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <textarea value={config.voicePolicy.concreteSlotFallbackPrompt || ""} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, concreteSlotFallbackPrompt: e.target.value } }))} rows={2} style={{ ...inputStyle, resize: "vertical" }} placeholder="Qué decir si rechaza los huecos" />
              <textarea value={config.voicePolicy.postVisitT2IntroPrompt || ""} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, postVisitT2IntroPrompt: e.target.value } }))} rows={2} style={{ ...inputStyle, resize: "vertical" }} placeholder="Arranque automático de T2 tras confirmar" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10 }}>
              <textarea value={config.voicePolicy.silenceRecoveryPrompt || ""} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, silenceRecoveryPrompt: e.target.value } }))} rows={2} style={{ ...inputStyle, resize: "vertical" }} placeholder="Silencio genérico" />
              <textarea value={config.voicePolicy.silenceRecoveryQuestionPrompt || ""} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, silenceRecoveryQuestionPrompt: e.target.value } }))} rows={2} style={{ ...inputStyle, resize: "vertical" }} placeholder="Silencio esperando duda" />
              <textarea value={config.voicePolicy.silenceRecoveryVisitPrompt || ""} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, silenceRecoveryVisitPrompt: e.target.value } }))} rows={2} style={{ ...inputStyle, resize: "vertical" }} placeholder="Silencio esperando sí a visita" />
              <textarea value={config.voicePolicy.silenceRecoverySlotPrompt || ""} onChange={(e) => setConfig((prev) => ({ ...prev, voicePolicy: { ...prev.voicePolicy, silenceRecoverySlotPrompt: e.target.value } }))} rows={2} style={{ ...inputStyle, resize: "vertical" }} placeholder="Silencio esperando respuesta a hueco" />
            </div>
          </div>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Telemarketing / T2</div>
        <div style={{ fontSize: 12, color: c.textSecondary, marginBottom: 12 }}>
          Instrucciones para conocer al cliente, recoger criterios y continuar después de una cita.
        </div>
        <div style={{ display: "grid", gap: 10 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>NOMBRE DEL MODO</label>
              <input value={config.telemarketingPolicy.modeLabel || ""} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, modeLabel: e.target.value } }))} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>CUÁNDO SE ACTIVA</label>
              <input value={config.telemarketingPolicy.activationInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, activationInstruction: e.target.value } }))} style={inputStyle} />
            </div>
          </div>
          <div>
            <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>PLANTILLA DE ENGANCHE T2</label>
            <textarea value={config.telemarketingPolicy.discardHookTemplate || ""} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, discardHookTemplate: e.target.value } }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>EJEMPLOS DE ENGANCHE (UNO POR LÍNEA)</label>
              <textarea value={(config.telemarketingPolicy.discardExamples || []).join("\n")} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, discardExamples: splitTextareaLines(e.target.value) } }))} rows={5} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>REGLAS COMUNES T2</label>
              <textarea value={config.telemarketingPolicy.commonRules || ""} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, commonRules: e.target.value } }))} rows={5} style={{ ...inputStyle, resize: "vertical" }} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <textarea value={config.telemarketingPolicy.afterVisitConfirmationInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, afterVisitConfirmationInstruction: e.target.value } }))} rows={6} style={{ ...inputStyle, resize: "vertical" }} placeholder="Al confirmar cita" />
            <textarea value={config.telemarketingPolicy.confirmedVisitModeInstruction || ""} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, confirmedVisitModeInstruction: e.target.value } }))} rows={6} style={{ ...inputStyle, resize: "vertical" }} placeholder="Modo post-cita" />
            <textarea value={config.telemarketingPolicy.confirmedVisitReminder || ""} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, confirmedVisitReminder: e.target.value } }))} rows={6} style={{ ...inputStyle, resize: "vertical" }} placeholder="Recordatorio final con cita confirmada" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <textarea value={config.telemarketingPolicy.resultsState?.zeroWithFilters || ""} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, resultsState: { ...prev.telemarketingPolicy.resultsState, zeroWithFilters: e.target.value } } }))} rows={4} style={{ ...inputStyle, resize: "vertical" }} placeholder="Cuando no hay resultados" />
            <textarea value={config.telemarketingPolicy.resultsState?.tooManyResults || ""} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, resultsState: { ...prev.telemarketingPolicy.resultsState, tooManyResults: e.target.value } } }))} rows={4} style={{ ...inputStyle, resize: "vertical" }} placeholder="Cuando hay demasiados resultados" />
            <textarea value={config.telemarketingPolicy.resultsState?.readyToShow || ""} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, resultsState: { ...prev.telemarketingPolicy.resultsState, readyToShow: e.target.value } } }))} rows={4} style={{ ...inputStyle, resize: "vertical" }} placeholder="Cuando ya puede enseñar propiedades" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <textarea value={(config.telemarketingPolicy.propertyPresentationRules || []).join("\n")} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, propertyPresentationRules: splitTextareaLines(e.target.value) } }))} rows={5} style={{ ...inputStyle, resize: "vertical" }} placeholder="Reglas para mostrar propiedades (una por línea)" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <textarea value={config.telemarketingPolicy.guides?.unknown || ""} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, guides: { ...prev.telemarketingPolicy.guides, unknown: e.target.value } } }))} rows={5} style={{ ...inputStyle, resize: "vertical" }} placeholder="Guía sin tipo" />
              <textarea value={config.telemarketingPolicy.guides?.generic || ""} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, guides: { ...prev.telemarketingPolicy.guides, generic: e.target.value } } }))} rows={5} style={{ ...inputStyle, resize: "vertical" }} placeholder="Guía genérica" />
              <textarea value={config.telemarketingPolicy.guides?.piso || ""} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, guides: { ...prev.telemarketingPolicy.guides, piso: e.target.value } } }))} rows={6} style={{ ...inputStyle, resize: "vertical" }} placeholder="Guía piso/apartamento" />
              <textarea value={config.telemarketingPolicy.guides?.chalet || ""} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, guides: { ...prev.telemarketingPolicy.guides, chalet: e.target.value } } }))} rows={6} style={{ ...inputStyle, resize: "vertical" }} placeholder="Guía chalet/casa" />
              <textarea value={config.telemarketingPolicy.guides?.local || ""} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, guides: { ...prev.telemarketingPolicy.guides, local: e.target.value } } }))} rows={5} style={{ ...inputStyle, resize: "vertical" }} placeholder="Guía local" />
              <textarea value={config.telemarketingPolicy.guides?.oficina || ""} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, guides: { ...prev.telemarketingPolicy.guides, oficina: e.target.value } } }))} rows={5} style={{ ...inputStyle, resize: "vertical" }} placeholder="Guía oficina" />
              <textarea value={config.telemarketingPolicy.guides?.nave || ""} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, guides: { ...prev.telemarketingPolicy.guides, nave: e.target.value } } }))} rows={5} style={{ ...inputStyle, resize: "vertical" }} placeholder="Guía nave" />
              <textarea value={config.telemarketingPolicy.guides?.terreno || ""} onChange={(e) => setConfig((prev) => ({ ...prev, telemarketingPolicy: { ...prev.telemarketingPolicy, guides: { ...prev.telemarketingPolicy.guides, terreno: e.target.value } } }))} rows={5} style={{ ...inputStyle, resize: "vertical" }} placeholder="Guía terreno/solar" />
            </div>
          </div>
        </div>
      </div>

      {veRespuestaLead && (
      <div style={cardStyle}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary }}>Cuándo contestar al lead</div>
            <div style={{ fontSize: 12, color: c.textSecondary }}>
              Define por días, varias franjas horarias y uno o varios medios con su propia temporización. En las pruebas actuales de voz solo se ejecuta la primera llamada.
            </div>
          </div>
          <button onClick={addResponseRule} disabled={!modAlgunaRespuestaLead} title={!modAlgunaRespuestaLead ? "No tienes permiso para modificar esta configuración" : undefined} style={{ background: c.bgInput, border: `1px solid ${c.sectionBorder}`, color: c.textPrimary, borderRadius: 8, padding: "7px 12px", cursor: modAlgunaRespuestaLead ? "pointer" : "not-allowed", fontSize: 12, opacity: modAlgunaRespuestaLead ? 1 : 0.5 }}>
            + Añadir regla
          </button>
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          {(config.responseRules || []).map((rule) => (
            <div key={rule.id} style={{ border: `1px solid ${c.sectionBorder}`, borderRadius: 10, padding: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1.2fr auto", gap: 12, marginBottom: 10, alignItems: "start" }}>
                <div>
                  <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>NOMBRE DE LA REGLA</label>
                  <input value={rule.name || ""} onChange={(e) => updateResponseRule(rule.id, { name: e.target.value })} style={inputStyle} placeholder="Ej: Lead de fin de semana" disabled={!modAlgunaRespuestaLead} />
                </div>
                <button onClick={() => removeResponseRule(rule.id)} disabled={!modAlgunaRespuestaLead} title={!modAlgunaRespuestaLead ? "No tienes permiso para modificar esta configuración" : undefined} style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#ef4444", borderRadius: 8, padding: "8px 12px", cursor: modAlgunaRespuestaLead ? "pointer" : "not-allowed", fontSize: 12, opacity: modAlgunaRespuestaLead ? 1 : 0.5 }}>
                  Eliminar
                </button>
              </div>

              {veHorario && (
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 6 }}>DÍAS EN LOS QUE APLICA</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {WEEKDAY_OPTIONS.map((day) => {
                    const active = (rule.days || []).includes(day.key);
                    return (
                      <button
                        key={day.key}
                        type="button"
                        onClick={() => modHorario && toggleResponseRuleDay(rule.id, day.key)}
                        disabled={!modHorario}
                        title={!modHorario ? "No tienes permiso para modificar días y horario de disponibilidad" : undefined}
                        style={{
                          background: active ? "#7c3aed22" : c.bgInput,
                          border: `1px solid ${active ? "#a855f7" : c.sectionBorder}`,
                          color: active ? "#a855f7" : c.textSecondary,
                          borderRadius: 999,
                          padding: "4px 10px",
                          cursor: modHorario ? "pointer" : "not-allowed",
                          fontSize: 12,
                          opacity: modHorario ? 1 : 0.6,
                        }}
                      >
                        {day.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              )}

              <div style={{ display: "grid", gap: 12, marginBottom: 10 }}>
                {(rule.windows || []).map((windowItem, windowIndex) => (
                  <div key={windowItem.id} style={{ border: `1px solid ${c.sectionBorder}`, borderRadius: 10, padding: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: c.textPrimary }}>
                        Franja {windowIndex + 1}
                      </div>
                      {veHorario && (
                      <div style={{ display: "flex", gap: 8 }}>
                        <button onClick={() => addResponseRuleRange(rule.id)} disabled={!modHorario} title={!modHorario ? "No tienes permiso para modificar días y horario de disponibilidad" : undefined} style={{ background: c.bgInput, border: `1px solid ${c.sectionBorder}`, color: c.textPrimary, borderRadius: 8, padding: "6px 10px", cursor: modHorario ? "pointer" : "not-allowed", fontSize: 12, opacity: modHorario ? 1 : 0.5 }}>
                          + Añadir franja
                        </button>
                        <button onClick={() => removeResponseRuleRange(rule.id, windowItem.id)} disabled={!modHorario} title={!modHorario ? "No tienes permiso para modificar días y horario de disponibilidad" : undefined} style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#ef4444", borderRadius: 8, padding: "6px 10px", cursor: modHorario ? "pointer" : "not-allowed", fontSize: 12, opacity: modHorario ? 1 : 0.5 }}>
                          Quitar franja
                        </button>
                      </div>
                      )}
                    </div>

                    {veHorario && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                      <div>
                        <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>DESDE</label>
                        <input type="time" value={windowItem.from || "00:00"} onChange={(e) => updateResponseRuleRange(rule.id, windowItem.id, { from: e.target.value })} style={inputStyle} disabled={!modHorario} />
                      </div>
                      <div>
                        <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>HASTA</label>
                        <input type="time" value={windowItem.to || "23:59"} onChange={(e) => updateResponseRuleRange(rule.id, windowItem.id, { to: e.target.value })} style={inputStyle} disabled={!modHorario} />
                      </div>
                    </div>
                    )}

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: c.textPrimary }}>Plan de medios de esta franja</div>
                      {veCanales && (
                      <button onClick={() => addResponseRuleStep(rule.id, windowItem.id)} disabled={!modCanales} title={!modCanales ? "No tienes permiso para modificar canal preferido/medio alternativo" : undefined} style={{ background: c.bgInput, border: `1px solid ${c.sectionBorder}`, color: c.textPrimary, borderRadius: 8, padding: "6px 10px", cursor: modCanales ? "pointer" : "not-allowed", fontSize: 12, opacity: modCanales ? 1 : 0.5 }}>
                        + Añadir medio
                      </button>
                      )}
                    </div>
                    <div style={{ display: "grid", gap: 12 }}>
                      {(windowItem.contactSteps || []).map((step, stepIndex) => (
                        <div key={step.id} style={{ border: `1px solid ${c.sectionBorder}`, borderRadius: 10, padding: 12 }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: c.textPrimary }}>
                              {stepIndex === 0 ? "Primer medio" : stepIndex === 1 ? "Segundo medio" : stepIndex === 2 ? "Tercer medio" : `Medio ${stepIndex + 1}`}
                            </div>
                            {veCanales && (
                            <button onClick={() => removeResponseRuleStep(rule.id, windowItem.id, step.id)} disabled={!modCanales} title={!modCanales ? "No tienes permiso para modificar canal preferido/medio alternativo" : undefined} style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#ef4444", borderRadius: 8, padding: "6px 10px", cursor: modCanales ? "pointer" : "not-allowed", fontSize: 12, opacity: modCanales ? 1 : 0.5 }}>
                              Quitar medio
                            </button>
                            )}
                          </div>
                          {veCanales && (
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                            <div>
                              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>MEDIO</label>
                              <select value={step.channel || "telefono"} onChange={(e) => updateResponseRuleStep(rule.id, windowItem.id, step.id, { channel: e.target.value })} style={inputStyle} disabled={!modCanales}>
                                {CHANNEL_OPTIONS.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>ALTERNATIVO SI NO DISPONEN</label>
                              <select value={step.fallbackChannel ?? "whatsapp"} onChange={(e) => updateResponseRuleStep(rule.id, windowItem.id, step.id, { fallbackChannel: e.target.value })} style={inputStyle} disabled={!modCanales}>
                                {FALLBACK_CHANNEL_OPTIONS.map((option) => <option key={option.key || "none"} value={option.key}>{option.label}</option>)}
                              </select>
                            </div>
                          </div>
                          )}
                          {veTiming && (
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                            <div>
                              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>CUÁNDO RESPONDER</label>
                              <select value={step.timingType || "immediate"} onChange={(e) => updateResponseRuleStep(rule.id, windowItem.id, step.id, { timingType: e.target.value })} style={inputStyle} disabled={!modTiming}>
                                {RESPONSE_TIMING_OPTIONS.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>VALOR</label>
                              <input
                                value={step.timingValue || ""}
                                onChange={(e) => updateResponseRuleStep(rule.id, windowItem.id, step.id, { timingValue: e.target.value })}
                                style={inputStyle}
                                placeholder={step.timingType === "after_minutes" ? "30" : "10:00"}
                                disabled={!modTiming}
                              />
                            </div>
                          </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>NOTAS</label>
                <textarea value={rule.notes || ""} onChange={(e) => updateResponseRule(rule.id, { notes: e.target.value })} rows={2} style={{ ...inputStyle, resize: "vertical" }} placeholder="Ej: si no contesta, luego ya seguirán las reglas de trabajo." disabled={!modAlgunaRespuestaLead} />
              </div>
            </div>
          ))}
        </div>
      </div>
      )}

      <div style={cardStyle}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: c.textPrimary }}>Cómo intentar agendar</div>
            <div style={{ fontSize: 12, color: c.textSecondary }}>
              Define la forma de proponer visita según el día y la hora en la que se está tratando el lead.
            </div>
          </div>
          <button onClick={addAgendaRule} style={{ background: c.bgInput, border: `1px solid ${c.sectionBorder}`, color: c.textPrimary, borderRadius: 8, padding: "7px 12px", cursor: "pointer", fontSize: 12 }}>
            + Añadir regla
          </button>
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          {(config.agendaRules || []).map((rule) => (
            <div key={rule.id} style={{ border: `1px solid ${c.sectionBorder}`, borderRadius: 10, padding: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1.2fr auto", gap: 12, marginBottom: 10, alignItems: "start" }}>
                <div>
                  <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>NOMBRE DE LA REGLA</label>
                  <input value={rule.name || ""} onChange={(e) => updateAgendaRule(rule.id, { name: e.target.value })} style={inputStyle} placeholder="Ej: Contacto de tarde" />
                </div>
                <button onClick={() => removeAgendaRule(rule.id)} style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#ef4444", borderRadius: 8, padding: "8px 12px", cursor: "pointer", fontSize: 12 }}>
                  Eliminar
                </button>
              </div>

              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 6 }}>DÍAS EN LOS QUE APLICA</label>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {WEEKDAY_OPTIONS.map((day) => {
                    const active = (rule.days || []).includes(day.key);
                    return (
                      <button
                        key={day.key}
                        type="button"
                        onClick={() => toggleAgendaRuleDay(rule.id, day.key)}
                        style={{
                          background: active ? "#7c3aed22" : c.bgInput,
                          border: `1px solid ${active ? "#a855f7" : c.sectionBorder}`,
                          color: active ? "#a855f7" : c.textSecondary,
                          borderRadius: 999,
                          padding: "4px 10px",
                          cursor: "pointer",
                          fontSize: 12,
                        }}
                      >
                        {day.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>DESDE</label>
                  <input type="time" value={rule.timeFrom || "09:30"} onChange={(e) => updateAgendaRule(rule.id, { timeFrom: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>HASTA</label>
                  <input type="time" value={rule.timeTo || "20:00"} onChange={(e) => updateAgendaRule(rule.id, { timeTo: e.target.value })} style={inputStyle} />
                </div>
                {veAgendaSlot && (
                <div>
                  <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>BASE DE PROPUESTA</label>
                  <select value={rule.slotBasis || "next_business_day"} onChange={(e) => updateAgendaRule(rule.id, { slotBasis: e.target.value })} style={inputStyle} disabled={!modAgendaSlot} title={!modAgendaSlot ? "No tienes permiso para modificar la base de hueco en agenda" : undefined}>
                    {AGENDA_SLOT_BASIS_OPTIONS.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
                  </select>
                </div>
                )}
                <div>
                  <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>CANAL</label>
                  <select value={rule.channel || "telefono"} onChange={(e) => updateAgendaRule(rule.id, { channel: e.target.value })} style={inputStyle}>
                    {CHANNEL_OPTIONS.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 10, marginBottom: 10 }}>
                <div>
                  <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>PRIMERA HORA A OFRECER</label>
                  <input type="time" value={rule.firstTime || "10:00"} onChange={(e) => updateAgendaRule(rule.id, { firstTime: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>SEGUNDA HORA A OFRECER</label>
                  <input type="time" value={rule.secondTime || "17:00"} onChange={(e) => updateAgendaRule(rule.id, { secondTime: e.target.value })} style={inputStyle} />
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: c.textSecondary, alignSelf: "end" }}>
                  <input
                    type="checkbox"
                    checked={rule.allowOpenAvailability === true}
                    onChange={(e) => updateAgendaRule(rule.id, { allowOpenAvailability: e.target.checked })}
                  />
                  Permitir disponibilidad abierta
                </label>
              </div>

              <div>
                <label style={{ fontSize: 11, color: c.textSecondary, display: "block", marginBottom: 4 }}>NOTAS</label>
                <textarea value={rule.notes || ""} onChange={(e) => updateAgendaRule(rule.id, { notes: e.target.value })} rows={2} style={{ ...inputStyle, resize: "vertical" }} placeholder="Ej: por la tarde, llevar la propuesta al siguiente laborable." />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={() => setConfig(parseLeadProcessConfig(null))} style={{ background: c.bgInput, border: `1px solid ${c.sectionBorder}`, color: c.textPrimary, borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontSize: 13 }}>
          Restaurar base
        </button>
        <button
          onClick={() => onGuardar(JSON.stringify(config))}
          disabled={guardando}
          style={{ background: "linear-gradient(135deg, #7c3aed, #a855f7)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
        >
          Guardar macroproceso
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// SUBCOMPONENTE — Diccionario multiidioma
// ─────────────────────────────────────────────
function SeccionTraducciones({ c, inputStyle, API, mostrarMensaje }) {
  const IDIOMAS = [
    { code: "fr", label: "Francés" },
    { code: "en", label: "Inglés" },
    { code: "de", label: "Alemán" },
    { code: "nl", label: "Holandés" },
    { code: "it", label: "Italiano" },
    { code: "pl", label: "Polaco" },
    { code: "ru", label: "Ruso" },
    { code: "uk", label: "Ucraniano" },
    { code: "zh", label: "Chino" },
  ];

  const CATEGORIAS = [
    { id: "tipo_inmueble", label: "Tipos de inmueble" },
    { id: "operacion", label: "Operaciones" },
    { id: "equipamiento", label: "Equipamiento" },
    { id: "poblacion", label: "Poblaciones" },
  ];

  const [traducciones, setTraducciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [filtroCat, setFiltroCat] = useState("tipo_inmueble");
  const [filtroIdioma, setFiltroIdioma] = useState("fr");
  const [editando, setEditando] = useState(null); // { id, sinonimos_texto }
  const [nuevaFila, setNuevaFila] = useState(null);

  useEffect(() => { cargarTraducciones(); }, []);

  async function cargarTraducciones() {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const res = await axios.get(`${API}/ana/traducciones`, { headers });
      setTraducciones(res.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function handleGenerar() {
    if (!(await dialogs.confirm("Claude generará traducciones para todos los valores del CRM. Puede tardar 20-30 segundos. ¿Continuar?"))) return;
    setGenerando(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const res = await axios.post(`${API}/ana/traducciones/generar`,
        { idiomas: IDIOMAS.map(i => i.code) }, { headers });
      mostrarMensaje(`✓ ${res.data.insertadas} traducciones generadas`, "ok");
      await cargarTraducciones();
    } catch (e) {
      mostrarMensaje("Error al generar traducciones", "error");
    } finally { setGenerando(false); }
  }

  async function handleGuardar(row, sinonimosTxt) {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const sinonimos = sinomimosTxt => sinomimosTxt.split(",").map(s => s.trim()).filter(Boolean);
      await axios.post(`${API}/ana/traducciones`, {
        categoria: row.categoria,
        valor_crm: row.valor_crm,
        idioma: row.idioma,
        sinonimos: sinonimos(sinonimosTxt),
      }, { headers });
      mostrarMensaje("Guardado", "ok");
      setEditando(null);
      await cargarTraducciones();
    } catch (e) { mostrarMensaje("Error al guardar", "error"); }
  }

  async function handleEliminar(id) {
    if (!(await dialogs.confirm("¿Eliminar esta entrada?"))) return;
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      await axios.delete(`${API}/ana/traducciones/${id}`, { headers });
      mostrarMensaje("Eliminado", "ok");
      await cargarTraducciones();
    } catch (e) { mostrarMensaje("Error al eliminar", "error"); }
  }

  async function handleNuevaGuardar() {
    if (!nuevaFila?.valor_crm || !nuevaFila?.sinonimos_texto) return;
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      await axios.post(`${API}/ana/traducciones`, {
        categoria: nuevaFila.categoria,
        valor_crm: nuevaFila.valor_crm,
        idioma: nuevaFila.idioma,
        sinonimos: nuevaFila.sinonimos_texto.split(",").map(s => s.trim()).filter(Boolean),
      }, { headers });
      mostrarMensaje("Añadido", "ok");
      setNuevaFila(null);
      await cargarTraducciones();
    } catch (e) { mostrarMensaje("Error al añadir", "error"); }
  }

  const filasFiltradas = traducciones.filter(t =>
    t.categoria === filtroCat && t.idioma === filtroIdioma
  );

  const thStyle = { padding: "8px 12px", fontSize: 11, fontWeight: 700, color: c.textSecondary, textAlign: "left", borderBottom: `1px solid ${c.sectionBorder}`, textTransform: "uppercase", letterSpacing: 1 };
  const tdStyle = { padding: "10px 12px", fontSize: 13, color: c.textPrimary, borderBottom: `1px solid ${c.sectionBorder}`, verticalAlign: "middle" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 900 }}>
      <div>
        <div style={{ fontSize: 16, fontWeight: 700, color: c.textPrimary, marginBottom: 4 }}>Diccionario multiidioma</div>
        <div style={{ fontSize: 13, color: c.textSecondary }}>Ana usa este diccionario para entender lo que el cliente pide en cualquier idioma y mapearlo al valor exacto del CRM.</div>
      </div>

      {/* Botón generar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={handleGenerar} disabled={generando}
          style={{ padding: "9px 20px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#7c3aed,#a855f7)", color: "#fff", fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: generando ? "not-allowed" : "pointer", opacity: generando ? 0.7 : 1, display: "flex", alignItems: "center", gap: 8 }}>
          {generando ? "⏳ Generando..." : "✨ Generar automáticamente con IA"}
        </button>
        <span style={{ fontSize: 12, color: c.textSecondary }}>Lee los valores del CRM y genera todos los sinónimos en todos los idiomas.</span>
      </div>

      {/* Filtros */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: c.textSecondary, display: "block", marginBottom: 4 }}>CATEGORÍA</label>
          <select value={filtroCat} onChange={e => setFiltroCat(e.target.value)}
            style={{ ...inputStyle, width: "auto", minWidth: 180 }}>
            {CATEGORIAS.map(cat => <option key={cat.id} value={cat.id}>{cat.label}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 700, color: c.textSecondary, display: "block", marginBottom: 4 }}>IDIOMA</label>
          <select value={filtroIdioma} onChange={e => setFiltroIdioma(e.target.value)}
            style={{ ...inputStyle, width: "auto", minWidth: 140 }}>
            {IDIOMAS.map(i => <option key={i.code} value={i.code}>{i.label}</option>)}
          </select>
        </div>
        <div style={{ alignSelf: "flex-end" }}>
          <button onClick={() => setNuevaFila({ categoria: filtroCat, idioma: filtroIdioma, valor_crm: "", sinonimos_texto: "" })}
            style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${c.sectionBorder}`, background: c.bgInput, color: c.textPrimary, fontFamily: "inherit", fontSize: 13, cursor: "pointer" }}>
            + Añadir entrada
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div style={{ background: c.bgPanel, border: `1px solid ${c.sectionBorder}`, borderRadius: 10, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 24, textAlign: "center", color: c.textSecondary, fontSize: 13 }}>Cargando...</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: c.bgInput }}>
                <th style={thStyle}>Valor CRM</th>
                <th style={thStyle}>Sinónimos (separados por coma)</th>
                <th style={{ ...thStyle, width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {/* Fila nueva */}
              {nuevaFila && (
                <tr>
                  <td style={tdStyle}>
                    <input value={nuevaFila.valor_crm} onChange={e => setNuevaFila(p => ({ ...p, valor_crm: e.target.value }))}
                      placeholder="ej: Chalet" style={{ ...inputStyle, padding: "5px 8px" }} />
                  </td>
                  <td style={tdStyle}>
                    <input value={nuevaFila.sinonimos_texto} onChange={e => setNuevaFila(p => ({ ...p, sinonimos_texto: e.target.value }))}
                      placeholder="ej: pavillon, villa, maison" style={{ ...inputStyle, padding: "5px 8px" }} />
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={handleNuevaGuardar} style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: "#7c3aed", color: "#fff", fontSize: 12, cursor: "pointer" }}>✓</button>
                      <button onClick={() => setNuevaFila(null)} style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${c.sectionBorder}`, background: "none", color: c.textSecondary, fontSize: 12, cursor: "pointer" }}>✗</button>
                    </div>
                  </td>
                </tr>
              )}
              {filasFiltradas.length === 0 && !nuevaFila ? (
                <tr><td colSpan={3} style={{ ...tdStyle, textAlign: "center", color: c.textSecondary, padding: 24 }}>
                  Sin entradas para esta categoría e idioma. Usa "Generar automáticamente" o añade una manualmente.
                </td></tr>
              ) : filasFiltradas.map(row => (
                <tr key={row.id}>
                  <td style={{ ...tdStyle, fontWeight: 600, width: 180 }}>{row.valor_crm}</td>
                  <td style={tdStyle}>
                    {editando?.id === row.id ? (
                      <input value={editando.sinonimos_texto}
                        onChange={e => setEditando(p => ({ ...p, sinonimos_texto: e.target.value }))}
                        style={{ ...inputStyle, padding: "5px 8px" }} autoFocus />
                    ) : (
                      <span style={{ color: c.textSecondary }}>{row.sinonimos?.join(", ") || "—"}</span>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {editando?.id === row.id ? (
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => handleGuardar(row, editando.sinonimos_texto)}
                          style={{ padding: "4px 10px", borderRadius: 6, border: "none", background: "#7c3aed", color: "#fff", fontSize: 12, cursor: "pointer" }}>✓</button>
                        <button onClick={() => setEditando(null)}
                          style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${c.sectionBorder}`, background: "none", color: c.textSecondary, fontSize: 12, cursor: "pointer" }}>✗</button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => setEditando({ id: row.id, sinonimos_texto: row.sinonimos?.join(", ") || "" })}
                          style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${c.sectionBorder}`, background: c.bgInput, color: c.textSecondary, fontSize: 12, cursor: "pointer" }}>Editar</button>
                        <button onClick={() => handleEliminar(row.id)}
                          style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.06)", color: "#ef4444", fontSize: 12, cursor: "pointer" }}>✗</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ fontSize: 12, color: c.textSecondary }}>
        💡 Los sinónimos se usan cuando el cliente escribe en otro idioma. Por ejemplo si un cliente francés escribe "pavillon" Ana lo mapea automáticamente a "Chalet" en el CRM.
      </div>
    </div>
  );
}

